<template>
  <div class="container py-4">
    <WizardHeader />

    <div class="card mb-4">
      <div class="card-body">
        <WizardProgress :steps="steps" :current-step="currentStep" @select="goToStep" />
      </div>
    </div>

    <!-- Wizard Content -->
    <div class="card">
      <div class="card-body p-0">
        <StepTeamSelection />

        <StepTemplateSelection v-if="hasMultipleTemplates && isCurrentStep('Template')" />

        <StepDraftSelection />

        <!-- Step: Quittung ausfüllen -->
        <div v-if="isCurrentStep('Quittung ausfüllen')" class="p-4">
          <h4 class="mb-3">
            <font-awesome-icon icon="edit" class="me-2 text-primary" />
            {{ documentFillStepTitle }}
          </h4>
          <p class="text-muted mb-4">
            Fülle die Formular-Daten in den untenstehenden Kategorien aus.
          </p>

          <!-- Sub-Steps Navigation -->
          <div v-if="subSteps.length > 0" class="mb-4">
            <div class="row">
              <div class="col-md-8">
                <div class="sub-steps-container">
                  <div class="row">
                    <div
                      v-for="(subStep, index) in subSteps"
                      :key="subStep.id"
                      class="col-md-4 mb-3"
                    >
                      <div
                        class="template-card sub-step-card"
                        :class="{
                          active: currentSubStep === subStep.id,
                          completed: isSubStepCompleted(subStep.category),
                          clickable: true,
                        }"
                        @click="currentSubStep = subStep.id"
                      >
                        <div class="template-card-header">
                          <div class="template-card-icon">
                            <font-awesome-icon
                              v-if="isSubStepCompleted(subStep.category)"
                              icon="check-circle"
                              class="text-success"
                            />
                            <font-awesome-icon
                              v-else
                              :icon="subStep.icon"
                              :class="currentSubStep === subStep.id ? 'text-primary' : 'text-muted'"
                            />
                          </div>
                          <div class="template-card-number">{{ subStep.id }}</div>
                        </div>
                        <div class="template-card-body">
                          <h6 class="template-card-title">{{ subStep.title }}</h6>
                          <p class="template-card-description">{{ subStep.description }}</p>
                          <div class="template-card-status">
                            <small class="text-muted">
                              {{ getSubStepCompletionStatus(subStep.category) }}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Sub-Step Navigation Buttons -->
                <div class="sub-step-navigation mt-3" v-if="subSteps.length > 1">
                  <div class="d-flex justify-content-between align-items-center">
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm"
                      @click="previousSubStep"
                      :disabled="currentSubStep === 1"
                    >
                      <font-awesome-icon icon="chevron-left" class="me-1" />
                      Vorheriger Bereich
                    </button>

                    <div class="sub-step-counter">
                      <small class="text-muted">
                        Bereich {{ currentSubStep }} von {{ subSteps.length }}
                      </small>
                    </div>

                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      @click="nextSubStep"
                      :disabled="currentSubStep === subSteps.length"
                    >
                      Nächster Bereich
                      <font-awesome-icon icon="chevron-right" class="ms-1" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- Receipt Summary Card -->
              <div class="col-md-4">
                <div class="card receipt-summary-card">
                  <div class="card-header">
                    <h6 class="mb-0">
                      <font-awesome-icon icon="calculator" class="me-2" />
                      {{ documentSumLabel }}
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="receipt-summary-item">
                      <div class="receipt-summary-label">Netto-Betrag:</div>
                      <div class="receipt-summary-value">
                        {{ formatAmount(formData.totalAmount) }} €
                      </div>
                    </div>
                    <div
                      v-if="formData.totalAmountWithTax > formData.totalAmount"
                      class="receipt-summary-item"
                    >
                      <div class="receipt-summary-label">MwSt.:</div>
                      <div class="receipt-summary-value">
                        {{ formatAmount(formData.totalAmountWithTax - formData.totalAmount) }} €
                      </div>
                    </div>
                    <div class="receipt-summary-item total">
                      <div class="receipt-summary-label">Gesamtbetrag:</div>
                      <div class="receipt-summary-value">
                        {{ formatAmount(formData.totalAmountWithTax) }} €
                      </div>
                    </div>

                    <div class="receipt-completion-status mt-3">
                      <div class="completion-indicator">
                        <div class="completion-bar">
                          <div
                            class="completion-progress"
                            :style="{ width: getOverallCompletionPercentage() + '%' }"
                          ></div>
                        </div>
                        <small class="text-muted">
                          {{ getOverallCompletionStatus() }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Content for current sub-step -->
          <div v-if="isCurrentSubStep('personal')" class="sub-step-content-area">
            <h5 class="mb-3">
              <font-awesome-icon
                :icon="getCategoryIcon('personal')"
                class="me-2 text-primary"
              />
              {{ getCategoryDisplayName('personal') }}
            </h5>
            <p class="text-muted mb-4">
              Fülle deine {{ getCategoryDisplayName('personal') }} aus. Diese
              Informationen werden in der {{ documentLabel }} verwendet.
            </p>

            <div class="card">
              <div class="card-body">
                <div
                  v-for="group in groupedTemplateFields.personal.groups"
                  :key="`personal-group-${group.id}`"
                  class="wizard-field-group mb-4"
                  :style="getGroupBlockStyle(group.color)"
                >
                  <div class="d-flex align-items-center gap-2 mb-3">
                    <span class="group-color-dot" :style="{ backgroundColor: group.color }"></span>
                    <h6 class="mb-0 text-uppercase">{{ group.name }}</h6>
                  </div>
                  <div class="row">
                    <div
                      v-for="field in group.fields"
                      :key="field.id"
                      :class="field.type === 'file' ? 'col-12' : 'col-md-6'"
                    >
                      <div class="mb-3">
                        <label :for="`field-${field.id}`" class="form-label">
                          {{ field.displayName }}
                          <span v-if="field.required" class="text-danger">*</span>
                        </label>

                        <!-- Text Input -->
                        <input
                          v-if="field.type === 'text'"
                          v-model="fieldData[field.name]"
                          type="text"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || field.displayName"
                          @input="clearFieldError(field.name, 'personal')"
                        />

                        <!-- Number Input -->
                        <input
                          v-else-if="field.type === 'number'"
                          v-model.number="fieldData[field.name]"
                          type="number"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || field.displayName"
                          step="0.01"
                          @input="clearFieldError(field.name, 'personal')"
                        />

                        <!-- Date Input -->
                        <template v-else-if="field.type === 'date'">
                          <input
                            v-model="fieldData[field.name]"
                            type="date"
                            class="form-control"
                            :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                            :id="`field-${field.id}`"
                            :required="field.required"
                            @input="clearFieldError(field.name, 'personal')"
                          />
                          <div
                            v-if="getDateWarningMessage(field.name)"
                            class="form-text text-warning mt-1 d-flex align-items-center"
                          >
                            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                            {{ getDateWarningMessage(field.name) }}
                          </div>
                        </template>

                        <!-- Select Input -->
                        <select
                          v-else-if="field.type === 'select'"
                          v-model="fieldData[field.name]"
                          class="form-select"
                          :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          @change="clearFieldError(field.name, 'personal')"
                        >
                          <option value="">-- Bitte wählen --</option>
                          <option
                            v-for="option in field.options?.options || []"
                            :key="option"
                            :value="option"
                          >
                            {{ option }}
                          </option>
                        </select>

                        <!-- Checkbox Input -->
                        <div v-else-if="field.type === 'checkbox'" class="form-check">
                          <input
                            v-model="fieldData[field.name]"
                            type="checkbox"
                            class="form-check-input"
                            :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                            :id="`field-${field.id}`"
                            :required="field.required"
                            @change="clearFieldError(field.name, 'personal')"
                          />
                          <label :for="`field-${field.id}`" class="form-check-label">
                            {{ field.displayName }}
                          </label>
                        </div>

                        <!-- Textarea Input -->
                        <textarea
                          v-else-if="field.type === 'textarea'"
                          v-model="fieldData[field.name]"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || field.displayName"
                          rows="3"
                          @input="clearFieldError(field.name, 'personal')"
                        ></textarea>

                        <!-- Location Input -->
                        <div v-else-if="field.type === 'location'" class="location-input-group">
                          <div class="input-group">
                            <input
                              v-model="fieldData[field.name]"
                              type="text"
                              class="form-control"
                              :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                              :id="`field-${field.id}`"
                              :required="field.required"
                              :placeholder="
                                field.placeholder || 'Standort eingeben oder automatisch ermitteln'
                              "
                              readonly
                            />
                            <button
                              type="button"
                              class="btn btn-outline-primary"
                              @click="getCurrentLocation(field.name)"
                              :disabled="locationLoading === field.name"
                              :title="
                                locationLoading === field.name
                                  ? 'Standort wird ermittelt...'
                                  : 'Aktuellen Standort ermitteln'
                              "
                            >
                              <font-awesome-icon
                                :icon="locationLoading === field.name ? 'spinner' : 'location-dot'"
                                :class="{ 'fa-spin': locationLoading === field.name }"
                              />
                            </button>
                          </div>
                          <div v-if="fieldData[field.name]" class="mt-2">
                            <small class="text-muted">
                              <font-awesome-icon icon="check-circle" class="text-success me-1" />
                              Standort erfolgreich ermittelt
                            </small>
                          </div>
                        </div>

                        <!-- File Upload -->
                        <div v-else-if="field.type === 'file'">
                          <!-- Check if team has pro/ultimate status or if it's a personal receipt -->
                          <div
                            v-if="
                              !formData.teamId ||
                              (selectedTeam &&
                                (selectedTeam.status === 'pro' || selectedTeam.status === 'ultimate'))
                            "
                          >
                            <AsyncFileUpload
                              :field-name="field.name"
                              :required="field.required"
                              :session-id="savedSessionIds[field.name]"
                              v-model:files="uploadedFiles[field.name]"
                              @validation-change="handleFileValidationChange(field.name, $event)"
                              @session-created="saveSessionId(field.name, $event)"
                              :ref="
                                (el) => {
                                  if (el) fileUploadRefs[field.name] = el
                                }
                              "
                            />
                          </div>
                          <div v-else class="alert alert-info">
                            <font-awesome-icon icon="info-circle" class="me-2" />
                            Datei-Upload ist nur für Pro- und Ultimate-Teams verfügbar.
                          </div>
                        </div>

                        <div v-if="errors[`personal-${field.name}`]" class="invalid-feedback">
                          {{ errors[`personal-${field.name}`] }}
                        </div>

                        <div v-if="field.description" class="form-text">
                          {{ field.description }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div
                    v-for="field in groupedTemplateFields.personal.ungrouped"
                    :key="field.id"
                    :class="field.type === 'file' ? 'col-12' : 'col-md-6'"
                  >
                    <div class="mb-3">
                      <label :for="`field-${field.id}`" class="form-label">
                        {{ field.displayName }}
                        <span v-if="field.required" class="text-danger">*</span>
                      </label>

                      <!-- Text Input -->
                      <input
                        v-if="field.type === 'text'"
                        v-model="fieldData[field.name]"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        :placeholder="field.placeholder || field.displayName"
                        @input="clearFieldError(field.name, 'personal')"
                      />

                      <!-- Number Input -->
                      <input
                        v-else-if="field.type === 'number'"
                        v-model.number="fieldData[field.name]"
                        type="number"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        :placeholder="field.placeholder || field.displayName"
                        step="0.01"
                        @input="clearFieldError(field.name, 'personal')"
                      />

                      <!-- Date Input -->
                      <template v-else-if="field.type === 'date'">
                        <input
                          v-model="fieldData[field.name]"
                          type="date"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          @input="clearFieldError(field.name, 'personal')"
                        />
                        <div
                          v-if="getDateWarningMessage(field.name)"
                          class="form-text text-warning mt-1 d-flex align-items-center"
                        >
                          <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                          {{ getDateWarningMessage(field.name) }}
                        </div>
                      </template>

                      <!-- Select Input -->
                      <select
                        v-else-if="field.type === 'select'"
                        v-model="fieldData[field.name]"
                        class="form-select"
                        :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        @change="clearFieldError(field.name, 'personal')"
                      >
                        <option value="">-- Bitte wählen --</option>
                        <option
                          v-for="option in field.options?.options || []"
                          :key="option"
                          :value="option"
                        >
                          {{ option }}
                        </option>
                      </select>

                      <!-- Checkbox Input -->
                      <div v-else-if="field.type === 'checkbox'" class="form-check">
                        <input
                          v-model="fieldData[field.name]"
                          type="checkbox"
                          class="form-check-input"
                          :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          @change="clearFieldError(field.name, 'personal')"
                        />
                        <label :for="`field-${field.id}`" class="form-check-label">
                          {{ field.displayName }}
                        </label>
                      </div>

                      <!-- Textarea Input -->
                      <textarea
                        v-else-if="field.type === 'textarea'"
                        v-model="fieldData[field.name]"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        :placeholder="field.placeholder || field.displayName"
                        rows="3"
                        @input="clearFieldError(field.name, 'personal')"
                      ></textarea>

                      <!-- Location Input -->
                      <div v-else-if="field.type === 'location'" class="location-input-group">
                        <div class="input-group">
                          <input
                            v-model="fieldData[field.name]"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': errors[`personal-${field.name}`] }"
                            :id="`field-${field.id}`"
                            :required="field.required"
                            :placeholder="
                              field.placeholder || 'Standort eingeben oder automatisch ermitteln'
                            "
                            readonly
                          />
                          <button
                            type="button"
                            class="btn btn-outline-primary"
                            @click="getCurrentLocation(field.name)"
                            :disabled="locationLoading === field.name"
                            :title="
                              locationLoading === field.name
                                ? 'Standort wird ermittelt...'
                                : 'Aktuellen Standort ermitteln'
                            "
                          >
                            <font-awesome-icon
                              :icon="locationLoading === field.name ? 'spinner' : 'location-dot'"
                              :class="{ 'fa-spin': locationLoading === field.name }"
                            />
                          </button>
                        </div>
                        <div v-if="fieldData[field.name]" class="mt-2">
                          <small class="text-muted">
                            <font-awesome-icon icon="check-circle" class="text-success me-1" />
                            Standort erfolgreich ermittelt
                          </small>
                        </div>
                      </div>

                      <!-- File Upload -->
                      <div v-else-if="field.type === 'file'">
                        <!-- Check if team has pro/ultimate status or if it's a personal receipt -->
                        <div
                          v-if="
                            !formData.teamId ||
                            (selectedTeam &&
                              (selectedTeam.status === 'pro' || selectedTeam.status === 'ultimate'))
                          "
                        >
                          <AsyncFileUpload
                            :field-name="field.name"
                            :required="field.required"
                            :session-id="savedSessionIds[field.name]"
                            v-model:files="uploadedFiles[field.name]"
                            @validation-change="handleFileValidationChange(field.name, $event)"
                            @session-created="saveSessionId(field.name, $event)"
                            :ref="
                              (el) => {
                                if (el) fileUploadRefs[field.name] = el
                              }
                            "
                          />
                        </div>
                        <div v-else class="alert alert-info">
                          <font-awesome-icon icon="info-circle" class="me-2" />
                          Datei-Upload ist nur für Pro- und Ultimate-Teams verfügbar.
                        </div>
                      </div>

                      <div v-if="errors[`personal-${field.name}`]" class="invalid-feedback">
                        {{ errors[`personal-${field.name}`] }}
                      </div>

                      <div v-if="field.description" class="form-text">
                        {{ field.description }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isCurrentSubStep('receipt')" class="sub-step-content-area">
            <h5 class="mb-3">
              <font-awesome-icon
                :icon="getCategoryIcon('receipt')"
                class="me-2 text-success"
              />
              {{ getCategoryDisplayName('receipt') }}
            </h5>
            <p class="text-muted mb-4">
              Gib die spezifischen Informationen für
              {{ getCategoryDisplayName('receipt') }} ein.
            </p>

            <div class="card">
              <div class="card-body">
                <div
                  v-for="group in groupedTemplateFields.receipt.groups"
                  :key="`receipt-group-${group.id}`"
                  class="wizard-field-group mb-4"
                  :style="getGroupBlockStyle(group.color)"
                >
                  <div class="d-flex align-items-center gap-2 mb-3">
                    <span class="group-color-dot" :style="{ backgroundColor: group.color }"></span>
                    <h6 class="mb-0 text-uppercase">{{ group.name }}</h6>
                  </div>
                  <div class="row">
                    <div
                      v-for="field in group.fields"
                      :key="field.id"
                      :class="
                        field.type === 'file' || field.type === 'repeater' ? 'col-12' : 'col-md-6'
                      "
                    >
                      <div class="mb-3">
                        <label :for="`field-${field.id}`" class="form-label">
                          {{ field.displayName }}
                          <span v-if="field.required" class="text-danger">*</span>
                        </label>

                        <!-- Text Input -->
                        <input
                          v-if="field.type === 'text'"
                          v-model="fieldData[field.name]"
                          type="text"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || field.displayName"
                          @input="clearFieldError(field.name, 'receipt')"
                        />

                        <!-- Number Input -->
                        <input
                          v-else-if="field.type === 'number'"
                          v-model.number="fieldData[field.name]"
                          type="number"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || field.displayName"
                          step="0.01"
                          @input="clearFieldError(field.name, 'receipt')"
                        />

                        <!-- Date Input -->
                        <template v-else-if="field.type === 'date'">
                          <input
                            v-model="fieldData[field.name]"
                            type="date"
                            class="form-control"
                            :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                            :id="`field-${field.id}`"
                            :required="field.required"
                            @input="clearFieldError(field.name, 'receipt')"
                          />
                          <div
                            v-if="getDateWarningMessage(field.name)"
                            class="form-text text-warning mt-1 d-flex align-items-center"
                          >
                            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                            {{ getDateWarningMessage(field.name) }}
                          </div>
                        </template>

                        <!-- Select Input -->
                        <select
                          v-else-if="field.type === 'select'"
                          v-model="fieldData[field.name]"
                          class="form-select"
                          :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          @change="clearFieldError(field.name, 'receipt')"
                        >
                          <option value="">-- Bitte wählen --</option>
                          <option
                            v-for="option in field.options?.options || []"
                            :key="option"
                            :value="option"
                          >
                            {{ option }}
                          </option>
                        </select>

                        <!-- Checkbox Input -->
                        <div v-else-if="field.type === 'checkbox'" class="form-check">
                          <input
                            v-model="fieldData[field.name]"
                            type="checkbox"
                            class="form-check-input"
                            :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                            :id="`field-${field.id}`"
                            :required="field.required"
                            @change="clearFieldError(field.name, 'receipt')"
                          />
                          <label :for="`field-${field.id}`" class="form-check-label">
                            {{ field.displayName }}
                          </label>
                        </div>

                        <!-- Textarea Input -->
                        <textarea
                          v-else-if="field.type === 'textarea'"
                          v-model="fieldData[field.name]"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || field.displayName"
                          rows="3"
                          @input="clearFieldError(field.name, 'receipt')"
                        ></textarea>

                        <!-- Location Input -->
                        <div v-else-if="field.type === 'location'" class="location-input-group">
                          <div class="input-group">
                            <input
                              v-model="fieldData[field.name]"
                              type="text"
                              class="form-control"
                              :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                              :id="`field-${field.id}`"
                              :required="field.required"
                              :placeholder="
                                field.placeholder || 'Standort eingeben oder automatisch ermitteln'
                              "
                              readonly
                            />
                            <button
                              type="button"
                              class="btn btn-outline-primary"
                              @click="getCurrentLocation(field.name)"
                              :disabled="locationLoading === field.name"
                              :title="
                                locationLoading === field.name
                                  ? 'Standort wird ermittelt...'
                                  : 'Aktuellen Standort ermitteln'
                              "
                            >
                              <font-awesome-icon
                                :icon="locationLoading === field.name ? 'spinner' : 'location-dot'"
                                :class="{ 'fa-spin': locationLoading === field.name }"
                              />
                            </button>
                          </div>
                          <div v-if="fieldData[field.name]" class="mt-2">
                            <small class="text-muted">
                              <font-awesome-icon icon="check-circle" class="text-success me-1" />
                              Standort erfolgreich ermittelt
                            </small>
                          </div>
                        </div>

                        <!-- File Upload -->
                        <div v-else-if="field.type === 'file'">
                          <!-- Check if team has pro/ultimate status or if it's a personal receipt -->
                          <div
                            v-if="
                              !formData.teamId ||
                              (selectedTeam &&
                                (selectedTeam.status === 'pro' || selectedTeam.status === 'ultimate'))
                            "
                          >
                            <AsyncFileUpload
                              :field-name="field.name"
                              :required="field.required"
                              :session-id="savedSessionIds[field.name]"
                              v-model:files="uploadedFiles[field.name]"
                              @validation-change="handleFileValidationChange(field.name, $event)"
                              @session-created="saveSessionId(field.name, $event)"
                              :ref="
                                (el) => {
                                  if (el) fileUploadRefs[field.name] = el
                                }
                              "
                            />
                          </div>
                          <div v-else class="alert alert-info">
                            <font-awesome-icon icon="info-circle" class="me-2" />
                            Datei-Upload ist nur für Pro- und Ultimate-Teams verfügbar.
                          </div>
                        </div>

                        <!-- Money Input -->
                        <template v-else-if="field.type === 'money'">
                          <div class="input-group">
                            <input
                              v-model.number="fieldData[field.name]"
                              type="number"
                              class="form-control"
                              :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                              :id="`field-${field.id}`"
                              :required="field.required"
                              :placeholder="field.placeholder || '0.00'"
                              step="0.01"
                              :min="field.validation?.min || 0"
                              :max="field.validation?.max"
                              @input="clearFieldError(field.name, 'receipt')"
                            />
                            <span v-if="field.options?.unit" class="input-group-text">{{
                              field.options.unit
                            }}</span>
                            <span
                              v-if="field.options?.unit && field.options?.pricePerUnit"
                              class="input-group-text"
                              >{{ field.options.pricePerUnit }} € / {{ field.options.unit }}</span
                            >
                          </div>

                          <div v-if="field.options?.includeTaxRate" class="mt-2">
                            <label class="form-label small text-muted">Mehrwertsteuersatz</label>

                            <select
                              v-if="field.options?.taxRateSelectionMode === 'user_choice'"
                              v-model="fieldData[`${field.name}_taxRate`]"
                              class="form-select form-select-sm"
                              :class="{ 'is-invalid': errors[`receipt-${field.name}_taxRate`] }"
                              :id="`field-${field.id}-taxrate`"
                              @change="
                                (onTaxRateChange(field.name, $event.target.value),
                                clearFieldError(field.name + '_taxRate', 'receipt'))
                              "
                            >
                              <option value="">-- MwSt-Satz wählen --</option>
                              <option
                                v-for="taxRate in field.options?.resolvedTaxRates || []"
                                :key="taxRate.id"
                                :value="taxRate.id"
                              >
                                {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                              </option>
                            </select>

                            <div
                              v-else-if="field.options?.selectedTaxRate"
                              class="form-control form-control-sm bg-light"
                            >
                              <small class="text-muted">
                                <font-awesome-icon icon="info-circle" class="me-1" />
                                {{
                                  field.options.selectedTaxRate.displayName ||
                                  field.options.resolvedTaxRates.filter(
                                    (tr) => tr.id === field.options.selectedTaxRateId,
                                  )[0].displayName
                                }}
                                ({{
                                  field.options.selectedTaxRate.rate ||
                                  field.options.resolvedTaxRates.filter(
                                    (tr) => tr.id === field.options.selectedTaxRateId,
                                  )[0].rate
                                }}%)
                              </small>
                            </div>

                            <div v-else class="form-control form-control-sm bg-light">
                              <small class="text-muted">
                                <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                                Keine Steuersätze verfügbar
                              </small>
                            </div>

                            <div
                              v-if="
                                getEffectiveTaxRate(field.name) &&
                                getEffectiveBruttoAmount(field.name) != null &&
                                !isTaxRateZero(field.name)
                              "
                              class="mt-2"
                            >
                              <div class="small text-muted">
                                <div v-if="!isTaxRateZero(field.name)">
                                  Netto: {{ formatAmount(calculateNettoAmount(field.name)) }} €
                                </div>
                                <div v-if="calculateTaxAmount(field.name) > 0">
                                  MwSt. ({{ getEffectiveTaxRate(field.name).rate }}%):
                                  {{ formatAmount(calculateTaxAmount(field.name)) }} €
                                </div>
                                <div class="brutto">
                                  Brutto: {{ formatAmount(getEffectiveBruttoAmount(field.name)) }} €
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>

                        <!-- Repeater Input -->
                        <div v-else-if="field.type === 'repeater'" class="repeater-field">
                          <div class="repeater-items">
                            <div
                              v-for="(item, index) in getRepeaterItems(field.name)"
                              :key="index"
                              class="repeater-item mb-3 p-3 border rounded"
                            >
                              <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="mb-0">Position {{ index + 1 }}</h6>
                                <button
                                  type="button"
                                  class="btn btn-sm btn-outline-danger"
                                  @click="removeRepeaterItem(field.name, index)"
                                  :disabled="
                                    field.options?.repeaterMinItems &&
                                    getRepeaterItems(field.name).length <=
                                      field.options.repeaterMinItems
                                  "
                                >
                                  <font-awesome-icon icon="trash" />
                                </button>
                              </div>

                              <div class="row">
                                <div class="col-md-3">
                                  <label class="form-label">Anzahl *</label>
                                  <input
                                    v-model.number="item.quantity"
                                    type="number"
                                    class="form-control"
                                    :class="{
                                      'is-invalid': errors[`${field.name}-${index}-quantity`],
                                    }"
                                    min="0"
                                    step="0.01"
                                    @input="clearRepeaterError(field.name, index, 'quantity')"
                                  />
                                  <div
                                    v-if="errors[`${field.name}-${index}-quantity`]"
                                    class="invalid-feedback"
                                  >
                                    {{ errors[`${field.name}-${index}-quantity`] }}
                                  </div>
                                </div>

                                <div class="col-md-3">
                                  <label class="form-label">Einheit</label>
                                  <input
                                    v-model="item.unit"
                                    type="text"
                                    class="form-control"
                                    placeholder="z.B. Stück, kg, m"
                                  />
                                </div>

                                <div class="col-md-4">
                                  <label class="form-label">Beschreibung *</label>
                                  <input
                                    v-model="item.description"
                                    type="text"
                                    class="form-control"
                                    :class="{
                                      'is-invalid': errors[`${field.name}-${index}-description`],
                                    }"
                                    placeholder="Beschreibung der Position"
                                    @input="clearRepeaterError(field.name, index, 'description')"
                                  />
                                  <div
                                    v-if="errors[`${field.name}-${index}-description`]"
                                    class="invalid-feedback"
                                  >
                                    {{ errors[`${field.name}-${index}-description`] }}
                                  </div>
                                </div>

                                <div class="col-md-2">
                                  <label class="form-label">Einzelpreis *</label>
                                  <div class="input-group">
                                    <input
                                      v-model.number="item.unitPrice"
                                      type="number"
                                      class="form-control"
                                      :class="{
                                        'is-invalid': errors[`${field.name}-${index}-unitPrice`],
                                      }"
                                      min="0"
                                      step="0.01"
                                      @input="clearRepeaterError(field.name, index, 'unitPrice')"
                                    />
                                    <span class="input-group-text">€</span>
                                  </div>
                                  <div
                                    v-if="errors[`${field.name}-${index}-unitPrice`]"
                                    class="invalid-feedback"
                                  >
                                    {{ errors[`${field.name}-${index}-unitPrice`] }}
                                  </div>
                                </div>
                              </div>

                              <!-- Tax Rate Selection -->
                              <div v-if="field.options?.repeaterIncludeTaxRate" class="row mt-2">
                                <div class="col-md-6">
                                  <label class="form-label">Steuersatz</label>
                                  <select
                                    v-if="
                                      field.options?.repeaterTaxRateSelectionMode === 'user_choice'
                                    "
                                    v-model="item.taxRateId"
                                    class="form-select"
                                    :class="{
                                      'is-invalid': errors[`${field.name}-${index}-taxRateId`],
                                    }"
                                    @change="
                                      clearRepeaterError(field.name, index, 'taxRateId');
                                      updateRepeaterTaxRateSelection(field.name, index, item.taxRateId)
                                    "
                                  >
                                    <option value="">-- MwSt-Satz wählen --</option>
                                    <option
                                      v-for="taxRate in (field.options?.repeaterResolvedTaxRates ||
                                        field.options?.resolvedTaxRates ||
                                        [])"
                                      :key="taxRate.id"
                                      :value="taxRate.id"
                                    >
                                      {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                                    </option>
                                  </select>
                                  <div
                                    v-else-if="field.options?.repeaterTaxRateSelectionMode === 'fixed'"
                                    class="form-control bg-light"
                                  >
                                    <small class="text-muted">
                                      {{
                                        getRepeaterTaxRateLabel(
                                          field,
                                          field.options?.repeaterSelectedTaxRateId,
                                        )
                                      }}
                                    </small>
                                  </div>
                                  <div
                                    v-else
                                    class="form-control bg-light"
                                  >
                                    <small class="text-muted">
                                      Kein Steuersatz erforderlich
                                    </small>
                                  </div>
                                  <div
                                    v-if="errors[`${field.name}-${index}-taxRateId`]"
                                    class="invalid-feedback"
                                  >
                                    {{ errors[`${field.name}-${index}-taxRateId`] }}
                                  </div>
                                </div>

                                <div class="col-md-6">
                                  <label class="form-label">Gesamtpreis</label>
                                  <div class="input-group">
                                    <input
                                      :value="formatCurrency(calculateRepeaterItemTotal(item, field))"
                                      type="text"
                                      class="form-control"
                                      readonly
                                    />
                                    <span class="input-group-text">€</span>
                                  </div>
                                </div>
                              </div>

                              <!-- Total without tax rate -->
                              <div v-else class="row mt-2">
                                <div class="col-md-6">
                                  <label class="form-label">Gesamtpreis</label>
                                  <div class="input-group">
                                    <input
                                      :value="formatCurrency(item.quantity * item.unitPrice)"
                                      type="text"
                                      class="form-control"
                                      readonly
                                    />
                                    <span class="input-group-text">€</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div class="d-flex justify-content-between align-items-center">
                              <button
                                type="button"
                                class="btn btn-outline-primary btn-sm"
                                @click="addRepeaterItem(field.name)"
                                :disabled="
                                  field.options?.repeaterMaxItems &&
                                  getRepeaterItems(field.name).length >=
                                    field.options.repeaterMaxItems
                                "
                              >
                                <font-awesome-icon icon="plus" class="me-1" />
                                Position hinzufügen
                              </button>

                              <div
                                v-if="field.options?.repeaterMaxItems"
                                class="text-muted small"
                              >
                              <span>
                              </span>
                                <span v-if="field.options?.repeaterMaxItems">
                                  Maximale Positionen: {{ field.options.repeaterMaxItems }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-if="errors[`receipt-${field.name}`]" class="invalid-feedback">
                          {{ errors[`receipt-${field.name}`] }}
                        </div>

                        <div v-if="field.description" class="form-text">
                          {{ field.description }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div
                    v-for="field in groupedTemplateFields.receipt.ungrouped"
                    :key="field.id"
                    :class="
                      field.type === 'file' || field.type === 'repeater' ? 'col-12' : 'col-md-6'
                    "
                  >
                    <div class="mb-3">
                      <label :for="`field-${field.id}`" class="form-label">
                        {{ field.displayName }}
                        <span v-if="field.required" class="text-danger">*</span>
                      </label>

                      <!-- Text Input -->
                      <input
                        v-if="field.type === 'text'"
                        v-model="fieldData[field.name]"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        :placeholder="field.placeholder || field.displayName"
                        @input="clearFieldError(field.name, 'receipt')"
                      />

                      <!-- Number Input -->
                      <input
                        v-else-if="field.type === 'number'"
                        v-model.number="fieldData[field.name]"
                        type="number"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        :placeholder="field.placeholder || field.displayName"
                        step="0.01"
                        @input="clearFieldError(field.name, 'receipt')"
                      />

                      <!-- Date Input -->
                      <template v-else-if="field.type === 'date'">
                        <input
                          v-model="fieldData[field.name]"
                          type="date"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          @input="clearFieldError(field.name, 'receipt')"
                        />
                        <div
                          v-if="getDateWarningMessage(field.name)"
                          class="form-text text-warning mt-1 d-flex align-items-center"
                        >
                          <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                          {{ getDateWarningMessage(field.name) }}
                        </div>
                      </template>

                      <!-- Select Input -->
                      <select
                        v-else-if="field.type === 'select'"
                        v-model="fieldData[field.name]"
                        class="form-select"
                        :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        @change="clearFieldError(field.name, 'receipt')"
                      >
                        <option value="">-- Bitte wählen --</option>
                        <option
                          v-for="option in field.options?.options || []"
                          :key="option"
                          :value="option"
                        >
                          {{ option }}
                        </option>
                      </select>

                      <!-- Checkbox Input -->
                      <div v-else-if="field.type === 'checkbox'" class="form-check">
                        <input
                          v-model="fieldData[field.name]"
                          type="checkbox"
                          class="form-check-input"
                          :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          @change="clearFieldError(field.name, 'receipt')"
                        />
                        <label :for="`field-${field.id}`" class="form-check-label">
                          {{ field.displayName }}
                        </label>
                      </div>

                      <!-- Textarea Input -->
                      <textarea
                        v-else-if="field.type === 'textarea'"
                        v-model="fieldData[field.name]"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        :placeholder="field.placeholder || field.displayName"
                        rows="3"
                        @input="clearFieldError(field.name, 'receipt')"
                      ></textarea>

                      <!-- Location Input -->
                      <div class="input-group" v-else-if="field.type === 'location'">
                        <input
                          v-model="fieldData[field.name]"
                          type="text"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`receipt-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          @input="clearFieldError(field.name, 'receipt')"
                          :placeholder="
                            field.placeholder || 'Standort eingeben oder automatisch ermitteln'
                          "
                        />
                        <button
                          type="button"
                          class="btn btn-outline-primary"
                          @click="getCurrentLocation(field.name)"
                          :disabled="locationLoading === field.name"
                          :title="
                            locationLoading === field.name
                              ? 'Standort wird ermittelt...'
                              : 'Aktuellen Standort ermitteln'
                          "
                        >
                          <font-awesome-icon
                            :icon="locationLoading === field.name ? 'spinner' : 'location-dot'"
                            :class="{ 'fa-spin': locationLoading === field.name }"
                          />
                        </button>
                      </div>
                      <div v-if="fieldData[field.name] && locationUsed[field.name]" class="mt-2">
                        <small class="text-muted">
                          <font-awesome-icon icon="check-circle" class="text-success me-1" />
                          Standort erfolgreich ermittelt
                        </small>
                      </div>

                      <!-- File Upload -->
                      <div v-else-if="field.type === 'file'">
                        <!-- Check if team has pro/ultimate status or if it's a personal receipt -->
                        <div
                          v-if="
                            !formData.teamId ||
                            (selectedTeam &&
                              (selectedTeam.status === 'pro' || selectedTeam.status === 'ultimate'))
                          "
                        >
                          <AsyncFileUpload
                            :field-name="field.name"
                            :required="field.required"
                            :session-id="savedSessionIds[field.name]"
                            v-model:files="uploadedFiles[field.name]"
                            @validation-change="handleFileValidationChange(field.name, $event)"
                            @session-created="saveSessionId(field.name, $event)"
                            :ref="
                              (el) => {
                                if (el) fileUploadRefs[field.name] = el
                              }
                            "
                          />
                        </div>
                        <div v-else class="alert alert-info">
                          <font-awesome-icon icon="info-circle" class="me-2" />
                          Datei-Upload ist nur für Pro- und Ultimate-Teams verfügbar.
                        </div>
                      </div>

                      <!-- Repeater Field -->
                      <div v-else-if="field.type === 'repeater'" class="repeater-field">
                        <div class="repeater-items">
                          <div
                            v-for="(item, index) in getRepeaterItems(field.name)"
                            :key="index"
                            class="repeater-item mb-3 p-3 border rounded"
                          >
                            <div class="d-flex justify-content-between align-items-center mb-2">
                              <h6 class="mb-0">Position {{ index + 1 }}</h6>
                              <button
                                type="button"
                                class="btn btn-sm btn-outline-danger"
                                @click="removeRepeaterItem(field.name, index)"
                                :disabled="
                                  field.options?.repeaterMinItems &&
                                  getRepeaterItems(field.name).length <=
                                    field.options.repeaterMinItems
                                "
                              >
                                <font-awesome-icon icon="trash" />
                              </button>
                            </div>

                            <div class="row">
                              <div class="col-md-3">
                                <label class="form-label">Anzahl *</label>
                                <input
                                  v-model.number="item.quantity"
                                  type="number"
                                  class="form-control"
                                  :class="{
                                    'is-invalid': errors[`${field.name}-${index}-quantity`],
                                  }"
                                  min="0"
                                  step="0.01"
                                  @input="clearRepeaterError(field.name, index, 'quantity')"
                                />
                                <div
                                  v-if="errors[`${field.name}-${index}-quantity`]"
                                  class="invalid-feedback"
                                >
                                  {{ errors[`${field.name}-${index}-quantity`] }}
                                </div>
                              </div>

                              <div class="col-md-3">
                                <label class="form-label">Einheit</label>
                                <input
                                  v-model="item.unit"
                                  type="text"
                                  class="form-control"
                                  placeholder="z.B. Stück, kg, m"
                                />
                              </div>

                              <div class="col-md-4">
                                <label class="form-label">Beschreibung *</label>
                                <input
                                  v-model="item.description"
                                  type="text"
                                  class="form-control"
                                  :class="{
                                    'is-invalid': errors[`${field.name}-${index}-description`],
                                  }"
                                  placeholder="Beschreibung der Position"
                                  @input="clearRepeaterError(field.name, index, 'description')"
                                />
                                <div
                                  v-if="errors[`${field.name}-${index}-description`]"
                                  class="invalid-feedback"
                                >
                                  {{ errors[`${field.name}-${index}-description`] }}
                                </div>
                              </div>

                              <div class="col-md-2">
                                <label class="form-label">Einzelpreis *</label>
                                <div class="input-group">
                                  <input
                                    v-model.number="item.unitPrice"
                                    type="number"
                                    class="form-control"
                                    :class="{
                                      'is-invalid': errors[`${field.name}-${index}-unitPrice`],
                                    }"
                                    min="0"
                                    step="0.01"
                                    @input="clearRepeaterError(field.name, index, 'unitPrice')"
                                  />
                                  <span class="input-group-text">€</span>
                                </div>
                                <div
                                  v-if="errors[`${field.name}-${index}-unitPrice`]"
                                  class="invalid-feedback"
                                >
                                  {{ errors[`${field.name}-${index}-unitPrice`] }}
                                </div>
                              </div>
                            </div>

                            <!-- Tax Rate Selection -->
                            <div v-if="field.options?.repeaterIncludeTaxRate" class="row mt-2">
                              <div class="col-md-6">
                                <label class="form-label">Steuersatz</label>
                                <select
                                  v-model="item.taxRateId"
                                  class="form-select"
                                  :class="{
                                    'is-invalid': errors[`${field.name}-${index}-taxRateId`],
                                  }"
                                  @change="clearRepeaterError(field.name, index, 'taxRateId')"
                                >
                                  <option value="">Steuersatz wählen</option>
                                  <option
                                    v-for="taxRate in getAvailableTaxRates(field)"
                                    :key="taxRate.id"
                                    :value="taxRate.id"
                                  >
                                    {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                                  </option>
                                </select>
                                <div
                                  v-if="errors[`${field.name}-${index}-taxRateId`]"
                                  class="invalid-feedback"
                                >
                                  {{ errors[`${field.name}-${index}-taxRateId`] }}
                                </div>
                              </div>

                              <div class="col-md-6">
                                <label class="form-label">Gesamtpreis</label>
                                <div class="input-group">
                                  <input
                                    :value="formatCurrency(calculateRepeaterItemTotal(item, field))"
                                    type="text"
                                    class="form-control"
                                    readonly
                                  />
                                  <span class="input-group-text">€</span>
                                </div>
                              </div>
                            </div>

                            <!-- Total without tax rate -->
                            <div v-else class="row mt-2">
                              <div class="col-md-6">
                                <label class="form-label">Gesamtpreis</label>
                                <div class="input-group">
                                  <input
                                    :value="formatCurrency(item.quantity * item.unitPrice)"
                                    type="text"
                                    class="form-control"
                                    readonly
                                  />
                                  <span class="input-group-text">€</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="d-flex justify-content-between align-items-center">
                          <button
                            type="button"
                            class="btn btn-outline-primary btn-sm"
                            @click="addRepeaterItem(field.name)"
                            :disabled="
                              field.options?.repeaterMaxItems &&
                              getRepeaterItems(field.name).length >= field.options.repeaterMaxItems
                            "
                          >
                            <font-awesome-icon icon="plus" class="me-1" />
                            Position hinzufügen
                          </button>

                          <div
                            v-if="
                              field.options?.repeaterMinItems || field.options?.repeaterMaxItems
                            "
                            class="text-muted small"
                          >
                            <span v-if="field.options?.repeaterMinItems">
                              Min: {{ field.options.repeaterMinItems }}
                            </span>
                            <span
                              v-if="
                                field.options?.repeaterMinItems && field.options?.repeaterMaxItems
                              "
                            >
                              |
                            </span>
                            <span v-if="field.options?.repeaterMaxItems">
                              Max: {{ field.options.repeaterMaxItems }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div v-if="errors[`receipt-${field.name}`]" class="invalid-feedback">
                        {{ errors[`receipt-${field.name}`] }}
                      </div>

                      <div v-if="field.description" class="form-text">
                        {{ field.description }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isCurrentSubStep('financial')" class="sub-step-content-area">
            <h5 class="mb-3">
              <font-awesome-icon
                :icon="getCategoryIcon('financial')"
                class="me-2 text-warning"
              />
              {{ getCategoryDisplayName('financial') }}
            </h5>
            <p class="text-muted mb-4">
              Gib alle {{ getCategoryDisplayName('financial') }} ein. Der Gesamtbetrag
              wird automatisch berechnet.
            </p>

            <div class="card">
              <div class="card-body">
                <div
                  v-for="group in groupedTemplateFields.financial.groups"
                  :key="`financial-group-${group.id}`"
                  class="wizard-field-group mb-4"
                  :style="getGroupBlockStyle(group.color)"
                >
                  <div class="d-flex align-items-center gap-2 mb-3">
                    <span class="group-color-dot" :style="{ backgroundColor: group.color }"></span>
                    <h6 class="mb-0 text-uppercase">{{ group.name }}</h6>
                  </div>
                  <div class="row">
                    <div
                      v-for="field in group.fields"
                      :key="field.id"
                      :class="field.type === 'file' ? 'col-12' : 'col-md-6'"
                    >
                      <div class="mb-3">
                        <label :for="`field-${field.id}`" class="form-label">
                          {{ field.displayName }}
                          <span v-if="field.required" class="text-danger">*</span>
                        </label>

                        <!-- Money Input -->
                        <div v-if="field.type === 'money'" class="input-group">
                          <input
                            v-model.number="fieldData[field.name]"
                            type="number"
                            class="form-control"
                            :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                            :id="`field-${field.id}`"
                            :required="field.required"
                            :placeholder="field.placeholder || '0.00'"
                            step="0.01"
                            :min="field.validation?.min || 0"
                            :max="field.validation?.max"
                            @input="clearFieldError(field.name, 'financial')"
                          />
                          <span v-if="field.options?.unit" class="input-group-text">{{
                            field.options.unit
                          }}</span>
                          <span
                            v-if="field.options?.unit && field.options?.pricePerUnit"
                            class="input-group-text"
                            >{{ field.options.pricePerUnit }} € / {{ field.options.unit }}</span
                          >
                        </div>

                        <div v-if="field.options?.includeTaxRate" class="mt-2">
                          <label class="form-label small text-muted">Mehrwertsteuersatz</label>

                          <select
                            v-if="field.options?.taxRateSelectionMode === 'user_choice'"
                            v-model="fieldData[`${field.name}_taxRate`]"
                            class="form-select form-select-sm"
                            :class="{ 'is-invalid': errors[`financial-${field.name}_taxRate`] }"
                            :id="`field-${field.id}-taxrate`"
                            @change="
                              (onTaxRateChange(field.name, $event.target.value),
                              clearFieldError(field.name + '_taxRate', 'financial'))
                            "
                          >
                            <option value="">-- MwSt-Satz wählen --</option>
                            <option
                              v-for="taxRate in field.options?.resolvedTaxRates || []"
                              :key="taxRate.id"
                              :value="taxRate.id"
                            >
                              {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                            </option>
                          </select>

                          <div
                            v-else-if="field.options?.selectedTaxRate"
                            class="form-control form-control-sm bg-light"
                          >
                            <small class="text-muted">
                              <font-awesome-icon icon="info-circle" class="me-1" />
                              {{
                                field.options.selectedTaxRate.displayName ||
                                field.options.resolvedTaxRates.filter(
                                  (tr) => tr.id === field.options.selectedTaxRateId,
                                )[0].displayName
                              }}
                              ({{
                                field.options.selectedTaxRate.rate ||
                                field.options.resolvedTaxRates.filter(
                                  (tr) => tr.id === field.options.selectedTaxRateId,
                                )[0].rate
                              }}%)
                            </small>
                          </div>

                          <div v-else class="form-control form-control-sm bg-light">
                            <small class="text-muted">
                              <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                              Keine Steuersätze verfügbar
                            </small>
                          </div>

                          <div
                            v-if="
                              getEffectiveTaxRate(field.name) &&
                              getEffectiveBruttoAmount(field.name) != null &&
                              !isTaxRateZero(field.name)
                            "
                            class="mt-2"
                          >
                            <div class="small text-muted">
                              <div v-if="!isTaxRateZero(field.name)">
                                Netto: {{ formatAmount(calculateNettoAmount(field.name)) }} €
                              </div>
                              <div v-if="calculateTaxAmount(field.name) > 0">
                                MwSt. ({{ getEffectiveTaxRate(field.name).rate }}%):
                                {{ formatAmount(calculateTaxAmount(field.name)) }} €
                              </div>
                              <div class="brutto">
                                Brutto: {{ formatAmount(getEffectiveBruttoAmount(field.name)) }} €
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Text Input -->
                        <input
                          v-else-if="field.type === 'text'"
                          v-model="fieldData[field.name]"
                          type="text"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || field.displayName"
                          @input="clearFieldError(field.name, 'financial')"
                        />

                        <!-- Number Input -->
                        <input
                          v-else-if="field.type === 'number'"
                          v-model.number="fieldData[field.name]"
                          type="number"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || field.displayName"
                          step="0.01"
                          @input="clearFieldError(field.name, 'financial')"
                        />

                        <!-- Date Input -->
                        <template v-else-if="field.type === 'date'">
                          <input
                            v-model="fieldData[field.name]"
                            type="date"
                            class="form-control"
                            :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                            :id="`field-${field.id}`"
                            :required="field.required"
                            @input="clearFieldError(field.name, 'financial')"
                          />
                          <div
                            v-if="getDateWarningMessage(field.name)"
                            class="form-text text-warning mt-1 d-flex align-items-center"
                          >
                            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                            {{ getDateWarningMessage(field.name) }}
                          </div>
                        </template>

                        <!-- Select Input -->
                        <select
                          v-else-if="field.type === 'select'"
                          v-model="fieldData[field.name]"
                          class="form-select"
                          :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          @change="clearFieldError(field.name, 'financial')"
                        >
                          <option value="">-- Bitte wählen --</option>
                          <option
                            v-for="option in field.options?.options || []"
                            :key="option"
                            :value="option"
                          >
                            {{ option }}
                          </option>
                        </select>

                        <!-- Checkbox Input -->
                        <div v-else-if="field.type === 'checkbox'" class="form-check">
                          <input
                            v-model="fieldData[field.name]"
                            type="checkbox"
                            class="form-check-input"
                            :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                            :id="`field-${field.id}`"
                            :required="field.required"
                            @change="clearFieldError(field.name, 'financial')"
                          />
                          <label :for="`field-${field.id}`" class="form-check-label">
                            {{ field.displayName }}
                          </label>
                        </div>

                        <!-- Textarea Input -->
                        <textarea
                          v-else-if="field.type === 'textarea'"
                          v-model="fieldData[field.name]"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || field.displayName"
                          rows="3"
                          @input="clearFieldError(field.name, 'financial')"
                        ></textarea>

                        <!-- Location Input -->
                        <div v-else-if="field.type === 'location'" class="location-input-group">
                          <div class="input-group">
                            <input
                              v-model="fieldData[field.name]"
                              type="text"
                              class="form-control"
                              :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                              :id="`field-${field.id}`"
                              :required="field.required"
                              :placeholder="
                                field.placeholder || 'Standort eingeben oder automatisch ermitteln'
                              "
                              readonly
                            />
                            <button
                              type="button"
                              class="btn btn-outline-primary"
                              @click="getCurrentLocation(field.name)"
                              :disabled="locationLoading === field.name"
                              :title="
                                locationLoading === field.name
                                  ? 'Standort wird ermittelt...'
                                  : 'Aktuellen Standort ermitteln'
                              "
                            >
                              <font-awesome-icon
                                :icon="locationLoading === field.name ? 'spinner' : 'location-dot'"
                                :class="{ 'fa-spin': locationLoading === field.name }"
                              />
                            </button>
                          </div>
                          <div v-if="fieldData[field.name]" class="mt-2">
                            <small class="text-muted">
                              <font-awesome-icon icon="check-circle" class="text-success me-1" />
                              Standort erfolgreich ermittelt
                            </small>
                          </div>
                        </div>

                        <!-- File Upload -->
                        <div v-else-if="field.type === 'file'">
                          <!-- Check if team has pro/ultimate status or if it's a personal receipt -->
                          <div
                            v-if="
                              !formData.teamId ||
                              (selectedTeam &&
                                (selectedTeam.status === 'pro' || selectedTeam.status === 'ultimate'))
                            "
                          >
                            <AsyncFileUpload
                              :field-name="field.name"
                              :required="field.required"
                              :session-id="savedSessionIds[field.name]"
                              v-model:files="uploadedFiles[field.name]"
                              @validation-change="handleFileValidationChange(field.name, $event)"
                              @session-created="saveSessionId(field.name, $event)"
                              :ref="
                                (el) => {
                                  if (el) fileUploadRefs[field.name] = el
                                }
                              "
                            />
                          </div>
                          <div v-else class="alert alert-info">
                            <font-awesome-icon icon="info-circle" class="me-2" />
                            Datei-Upload ist nur für Pro- und Ultimate-Teams verfügbar.
                          </div>
                        </div>


                        <!-- Tax Rate Selection (for fields with includeTaxRate) -->
                        <div v-if="field.options?.includeTaxRate" class="mt-2">
                          <label class="form-label small text-muted">Mehrwertsteuersatz</label>

                          <select
                            v-if="field.options?.taxRateSelectionMode === 'user_choice'"
                            v-model="fieldData[`${field.name}_taxRate`]"
                            class="form-select form-select-sm"
                            :class="{ 'is-invalid': errors[`financial-${field.name}_taxRate`] }"
                            :id="`field-${field.id}-taxrate`"
                            @change="
                              (onTaxRateChange(field.name, $event.target.value),
                              clearFieldError(field.name + '_taxRate', 'financial'))
                            "
                          >
                            <option value="">-- MwSt-Satz wählen --</option>
                            <option
                              v-for="taxRate in field.options?.resolvedTaxRates || []"
                              :key="taxRate.id"
                              :value="taxRate.id"
                            >
                              {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                            </option>
                          </select>

                          <div
                            v-else-if="field.options?.selectedTaxRate"
                            class="form-control form-control-sm bg-light"
                          >
                            <small class="text-muted">
                              <font-awesome-icon icon="info-circle" class="me-1" />
                              {{
                                field.options.selectedTaxRate.displayName ||
                                field.options.resolvedTaxRates.filter(
                                  (tr) => tr.id === field.options.selectedTaxRateId,
                                )[0].displayName
                              }}
                              ({{
                                field.options.selectedTaxRate.rate ||
                                field.options.resolvedTaxRates.filter(
                                  (tr) => tr.id === field.options.selectedTaxRateId,
                                )[0].rate
                              }}%)
                            </small>
                          </div>

                          <div v-else class="form-control form-control-sm bg-light">
                            <small class="text-muted">
                              <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                              Keine Steuersätze verfügbar
                            </small>
                          </div>

                          <div
                            v-if="
                              getEffectiveTaxRate(field.name) &&
                              getEffectiveBruttoAmount(field.name) != null &&
                              !isTaxRateZero(field.name)
                            "
                            class="mt-2"
                          >
                            <div class="small text-muted">
                              <div v-if="!isTaxRateZero(field.name)">
                                Netto: {{ formatAmount(calculateNettoAmount(field.name)) }} €
                              </div>
                              <div v-if="calculateTaxAmount(field.name) > 0">
                                MwSt. ({{ getEffectiveTaxRate(field.name).rate }}%):
                                {{ formatAmount(calculateTaxAmount(field.name)) }} €
                              </div>
                              <div class="brutto">
                                Brutto: {{ formatAmount(getEffectiveBruttoAmount(field.name)) }} €
                              </div>
                            </div>
                          </div>
                        </div>

                        <div v-if="errors[`financial-${field.name}`]" class="invalid-feedback">
                          {{ errors[`financial-${field.name}`] }}
                        </div>
                        <div
                          v-if="errors[`financial-${field.name}_taxRate`]"
                          class="invalid-feedback"
                        >
                          {{ errors[`financial-${field.name}_taxRate`] }}
                        </div>

                        <div v-if="field.description" class="form-text">
                          {{ field.description }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div
                    v-for="field in groupedTemplateFields.financial.ungrouped"
                    :key="field.id"
                    :class="field.type === 'file' ? 'col-12' : 'col-md-6'"
                  >
                    <div class="mb-3">
                      <label :for="`field-${field.id}`" class="form-label">
                        {{ field.displayName }}
                        <span v-if="field.required" class="text-danger">*</span>
                      </label>

                      <!-- Money Input -->
                      <div v-if="field.type === 'money'" class="input-group">
                        <input
                          v-model.number="fieldData[field.name]"
                          type="number"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          :placeholder="field.placeholder || '0.00'"
                          step="0.01"
                          :min="field.validation?.min || 0"
                          :max="field.validation?.max"
                          @input="clearFieldError(field.name, 'financial')"
                        />
                        <span v-if="field.options?.unit" class="input-group-text">{{
                          field.options.unit
                        }}</span>
                        <span
                          v-if="field.options?.unit && field.options?.pricePerUnit"
                          class="input-group-text"
                          >{{ field.options.pricePerUnit }} € / {{ field.options.unit }}</span
                        >
                      </div>

                      <!-- Number Input -->
                      <input
                        v-else-if="field.type === 'number'"
                        v-model.number="fieldData[field.name]"
                        type="number"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        :placeholder="field.placeholder || field.displayName"
                        step="0.01"
                        :min="field.validation?.min"
                        :max="field.validation?.max"
                        @input="clearFieldError(field.name, 'financial')"
                      />

                      <!-- Text Input -->
                      <input
                        v-else-if="field.type === 'text'"
                        v-model="fieldData[field.name]"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        :placeholder="field.placeholder || field.displayName"
                        @input="clearFieldError(field.name, 'financial')"
                      />

                      <!-- Select Input -->
                      <select
                        v-else-if="field.type === 'select'"
                        v-model="fieldData[field.name]"
                        class="form-select"
                        :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        @change="clearFieldError(field.name, 'financial')"
                      >
                        <option value="">-- Bitte wählen --</option>
                        <option
                          v-for="option in field.options?.options || []"
                          :key="option"
                          :value="option"
                        >
                          {{ option }}
                        </option>
                      </select>

                      <!-- Checkbox Input -->
                      <div v-else-if="field.type === 'checkbox'" class="form-check">
                        <input
                          v-model="fieldData[field.name]"
                          type="checkbox"
                          class="form-check-input"
                          :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                          :id="`field-${field.id}`"
                          :required="field.required"
                          @change="clearFieldError(field.name, 'financial')"
                        />
                        <label :for="`field-${field.id}`" class="form-check-label">
                          {{ field.displayName }}
                        </label>
                      </div>

                      <!-- Textarea Input -->
                      <textarea
                        v-else-if="field.type === 'textarea'"
                        v-model="fieldData[field.name]"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                        :id="`field-${field.id}`"
                        :required="field.required"
                        :placeholder="field.placeholder || field.displayName"
                        rows="3"
                        @input="clearFieldError(field.name, 'financial')"
                      ></textarea>

                      <!-- Location Input -->
                      <div v-else-if="field.type === 'location'" class="location-input-group">
                        <div class="input-group">
                          <input
                            v-model="fieldData[field.name]"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': errors[`financial-${field.name}`] }"
                            :id="`field-${field.id}`"
                            :required="field.required"
                            :placeholder="
                              field.placeholder || 'Standort eingeben oder automatisch ermitteln'
                            "
                            readonly
                          />
                          <button
                            type="button"
                            class="btn btn-outline-primary"
                            @click="getCurrentLocation(field.name)"
                            :disabled="locationLoading === field.name"
                            :title="
                              locationLoading === field.name
                                ? 'Standort wird ermittelt...'
                                : 'Aktuellen Standort ermitteln'
                            "
                          >
                            <font-awesome-icon
                              :icon="locationLoading === field.name ? 'spinner' : 'location-dot'"
                              :class="{ 'fa-spin': locationLoading === field.name }"
                            />
                          </button>
                        </div>
                        <div v-if="fieldData[field.name]" class="mt-2">
                          <small class="text-muted">
                            <font-awesome-icon icon="check-circle" class="text-success me-1" />
                            Standort erfolgreich ermittelt
                          </small>
                        </div>
                      </div>

                      <!-- File Upload -->
                      <div v-else-if="field.type === 'file'">
                        <!-- Check if team has pro/ultimate status or if it's a personal receipt -->
                        <div
                          v-if="
                            !formData.teamId ||
                            (selectedTeam &&
                              (selectedTeam.status === 'pro' || selectedTeam.status === 'ultimate'))
                          "
                        >
                          <AsyncFileUpload
                            :field-name="field.name"
                            :required="field.required"
                            :session-id="savedSessionIds[field.name]"
                            v-model:files="uploadedFiles[field.name]"
                            @validation-change="handleFileValidationChange(field.name, $event)"
                            @session-created="saveSessionId(field.name, $event)"
                            :ref="
                              (el) => {
                                if (el) fileUploadRefs[field.name] = el
                              }
                            "
                          />
                        </div>
                        <div v-else class="alert alert-info">
                          <font-awesome-icon icon="info-circle" class="me-2" />
                          Datei-Upload ist nur für Pro- und Ultimate-Teams verfügbar.
                        </div>
                      </div>

                      <!-- Tax Rate Selection (for fields with includeTaxRate) -->
                      <div v-if="field.options?.includeTaxRate" class="mt-2">
                        <label class="form-label small text-muted">Mehrwertsteuersatz</label>

                        <!-- User Choice: Dropdown selection -->
                        <select
                          v-if="field.options?.taxRateSelectionMode === 'user_choice'"
                          v-model="fieldData[`${field.name}_taxRate`]"
                          class="form-select form-select-sm"
                          :class="{ 'is-invalid': errors[`financial-${field.name}_taxRate`] }"
                          :id="`field-${field.id}-taxrate`"
                          @change="
                            (onTaxRateChange(field.name, $event.target.value),
                            clearFieldError(field.name + '_taxRate', 'financial'))
                          "
                        >
                          <option value="">-- MwSt-Satz wählen --</option>
                          <option
                            v-for="taxRate in field.options?.resolvedTaxRates || []"
                            :key="taxRate.id"
                            :value="taxRate.id"
                          >
                            {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                          </option>
                        </select>

                        <!-- Pre-selected: Display only -->
                        <div
                          v-else-if="field.options?.selectedTaxRate"
                          class="form-control form-control-sm bg-light"
                        >
                          <small class="text-muted">
                            <font-awesome-icon icon="info-circle" class="me-1" />
                            {{
                              field.options.selectedTaxRate.displayName ||
                              field.options.resolvedTaxRates.filter(
                                (tr) => tr.id === field.options.selectedTaxRateId,
                              )[0].displayName
                            }}
                            ({{
                              field.options.selectedTaxRate.rate ||
                              field.options.resolvedTaxRates.filter(
                                (tr) => tr.id === field.options.selectedTaxRateId,
                              )[0].rate
                            }}%)
                          </small>
                        </div>

                        <!-- Fallback: No tax rates available -->
                        <div v-else class="form-control form-control-sm bg-light">
                          <small class="text-muted">
                            <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                            Keine Steuersätze verfügbar
                          </small>
                        </div>

                        <!-- Tax calculation display -->
                        <div
                          v-if="
                            getEffectiveTaxRate(field.name) &&
                            getEffectiveBruttoAmount(field.name) != null &&
                            !isTaxRateZero(field.name)
                          "
                          class="mt-2"
                        >
                          <div class="small text-muted">
                            <div v-if="!isTaxRateZero(field.name)">
                              Netto: {{ formatAmount(calculateNettoAmount(field.name)) }} €
                            </div>
                            <div v-if="calculateTaxAmount(field.name) > 0">
                              MwSt. ({{ getEffectiveTaxRate(field.name).rate }}%):
                              {{ formatAmount(calculateTaxAmount(field.name)) }} €
                            </div>
                            <div class="brutto">
                              Brutto: {{ formatAmount(getEffectiveBruttoAmount(field.name)) }} €
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="errors[`financial-${field.name}`]" class="invalid-feedback">
                        {{ errors[`financial-${field.name}`] }}
                      </div>
                      <div
                        v-if="errors[`financial-${field.name}_taxRate`]"
                        class="invalid-feedback"
                      >
                        {{ errors[`financial-${field.name}_taxRate`] }}
                      </div>

                      <div v-if="field.description" class="form-text">
                        {{ field.description }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Sub-Steps Available -->
          <div v-if="subSteps.length === 0" class="text-center py-4">
            <font-awesome-icon icon="info-circle" size="2x" class="text-muted mb-3" />
            <p class="text-muted">
              Keine Formular-Felder verfügbar. Fahre mit dem nächsten Schritt fort.
            </p>
          </div>
        </div>

        <StepSignature />

        <StepCopy />

        <StepReview />

        <WizardFooter />
      </div>
    </div>
  </div>
  <!-- Field Filter Modal -->
  <Teleport to="#modalTarget">
    <div v-if="showFieldFilterModal" class="modal show d-block p-4" style="z-index: 1071">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <font-awesome-icon icon="filter" class="me-2" />
              Erweiterte Filter
            </h5>
            <button type="button" class="btn-close" @click="showFieldFilterModal = false"></button>
          </div>
          <div class="modal-body">
            <!-- Loading Overlay for Auto-Reload -->
            <div
              v-if="filterModalLoading"
              class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center filter-loading-overlay"
              style="top: 0; left: 0; z-index: 10"
            >
              <div class="text-center">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Lade...</span>
                </div>
                <p class="mt-2 text-muted">Filter werden angewendet...</p>
              </div>
            </div>

            <!-- Datum Filter -->
            <div class="row g-3 mb-4">
              <div class="col-md-6">
                <label class="form-label fw-bold">Datum von</label>
                <input
                  v-model="draftFilters.dateFrom"
                  type="date"
                  class="form-control"
                  @focus="openDatePicker"
                  @click="openDatePicker"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-bold">Datum bis</label>
                <input
                  v-model="draftFilters.dateTo"
                  type="date"
                  class="form-control"
                  @focus="openDatePicker"
                  @click="openDatePicker"
                />
              </div>
            </div>

            <!-- Verfügbare Feld-Filter -->
            <div v-if="Object.keys(availableFilters.fieldValues).length > 0" class="mb-4">
              <label class="form-label fw-bold">Feld-Filter</label>
              <div class="row g-3">
                <div
                  v-for="(fieldInfo, fieldName) in availableFilters.fieldValues"
                  :key="fieldName"
                  class="col-md-6"
                >
                  <div class="form-group">
                    <label class="form-label small fw-semibold">
                      {{ fieldInfo.displayName }}
                      <span class="text-muted"
                        >({{ fieldInfo.values ? fieldInfo.values.length : 0 }} Optionen)</span
                      >
                    </label>

                    <!-- Multi-Select für Feld-Werte -->
                    <div class="multiselect-container">
                      <div class="multiselect-input" @click="toggleMultiselect(fieldName)">
                        <div class="multiselect-selected">
                          <span
                            v-if="
                              !draftFilters.fields[fieldName] ||
                              draftFilters.fields[fieldName].length === 0
                            "
                            class="text-muted"
                          >
                            Alle auswählen...
                          </span>
                          <span v-else class="selected-count">
                            {{ draftFilters.fields[fieldName].length }} ausgewählt
                          </span>
                        </div>
                        <font-awesome-icon icon="chevron-down" class="multiselect-arrow" />
                      </div>

                      <!-- Dropdown -->
                      <div v-if="openMultiselect === fieldName" class="multiselect-dropdown">
                        <!-- Search -->
                        <div class="multiselect-search">
                          <input
                            v-model="multiselectSearch[fieldName]"
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="Suchen..."
                            @click.stop
                          />
                        </div>

                        <!-- Options -->
                        <div class="multiselect-options">
                          <div
                            v-for="option in getFilteredOptions(fieldName)"
                            :key="option.value"
                            class="multiselect-option"
                            @click="toggleOption(fieldName, option.value)"
                          >
                            <div class="form-check">
                              <input
                                :checked="isOptionSelected(fieldName, option.value)"
                                type="checkbox"
                                class="form-check-input"
                                @click.stop
                              />
                              <label class="form-check-label">
                                {{ option.label }}
                              </label>
                            </div>
                          </div>
                        </div>

                        <!-- Actions -->
                        <div class="multiselect-actions">
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            @click="clearFieldOptions(fieldName)"
                          >
                            löschen
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Active Filters Summary -->
            <div v-if="activeFiltersCount > 0" class="mb-3">
              <label class="form-label fw-bold">Aktive Filter ({{ activeFiltersCount }})</label>

              <!-- Date Filters -->
              <div v-if="draftFilters.dateFrom || draftFilters.dateTo" class="mb-2">
                <small class="text-muted fw-semibold">Datums-Filter:</small>
                <div class="d-flex flex-wrap gap-2 mt-1">
                  <span v-if="draftFilters.dateFrom" class="badge bg-info">
                    Von: {{ draftFilters.dateFrom }}
                    <button
                      type="button"
                      class="btn-close btn-close-white ms-1"
                      style="font-size: 0.5rem"
                      @click="draftFilters.dateFrom = ''"
                    ></button>
                  </span>

                  <span v-if="draftFilters.dateTo" class="badge bg-info">
                    Bis: {{ draftFilters.dateTo }}
                    <button
                      type="button"
                      class="btn-close btn-close-white ms-1"
                      style="font-size: 0.5rem"
                      @click="draftFilters.dateTo = ''"
                    ></button>
                  </span>
                </div>
              </div>

              <!-- Field Filters: flach, nicht nach Kategorie gruppiert -->
              <div class="d-flex flex-wrap gap-1 mt-1">
                <span
                  v-for="entry in activeFieldFilterList"
                  :key="entry.fieldName + ':' + entry.value"
                  class="badge bg-primary"
                  style="font-size: 0.75rem"
                >
                  {{ getFieldDisplayName(entry.fieldName) }}: {{ entry.label }}
                  <button
                    type="button"
                    class="btn-close btn-close-white ms-1"
                    style="font-size: 0.5rem"
                    @click="removeOption(entry.fieldName, entry.value)"
                  ></button>
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="clearDraftFilters">
              Alle Filter löschen
            </button>
            <button type="button" class="btn btn-primary" @click="showFieldFilterModal = false">
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
  <div v-if="showDraftSelection" class="modal draft-selection-modal p-4" style="z-index: 1070">
    <div class="card">
      <!-- ───── Header ───── -->
      <div class="card-header d-flex">
        <h5 class="my-2">
          <font-awesome-icon icon="file-alt" class="me-2" />
          Vorlage auswählen
        </h5>
        <button
          type="button"
          class="btn-close ms-auto my-2"
          @click="showDraftSelection = false"
        ></button>
      </div>

      <!-- ───── Body ───── -->
      <div class="card-body">
        <!-- Search & Filters -->
        <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
          <div class="flex-grow-1" style="min-width: 200px">
            <input
              v-model="draftSearchQuery"
              type="text"
              class="form-control"
              placeholder="Vorlagen durchsuchen..."
              @input="debouncedDraftSearch"
            />
          </div>

          <!-- Results Info -->
          <div class="d-flex align-items-center">
            <small class="text-muted">
              <span v-if="draftPagination.filteredItems !== undefined">
                {{ draftPagination.filteredItems }} von {{ draftPagination.totalItems }} Vorlagen
              </span>
              <span v-else> {{ drafts.length }} Vorlagen </span>
            </small>
          </div>

          <div class="d-flex align-items-center">
            <button
              class="btn btn-outline-primary position-relative"
              @click="showFieldFilterModal = true"
            >
              <font-awesome-icon icon="filter" class="me-1" />
              <span class="d-md-none d-inline">Feld-Filter</span>
              <span class="d-none d-md-inline">Filter</span>
              <span
                v-if="activeFiltersCount > 0"
                class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                style="font-size: 0.6rem"
              >
                {{ activeFiltersCount }}
              </span>
            </button>
          </div>

          <div class="d-flex align-items-center">
            <button class="btn btn-outline-secondary" @click="clearDraftFilters">
              <font-awesome-icon icon="times" class="me-1" />
              <span class="d-md-none d-inline">Filter löschen</span>
              <span class="d-none d-md-inline">Löschen</span>
            </button>
          </div>
        </div>

        <!-- Loading state -->
        <div v-if="draftsLoading" class="text-center py-5">
          <div class="loading-animation">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
            <h5 class="mt-3 mb-2">Vorlagen werden geladen</h5>
            <p class="text-muted mb-0">Bitte warte einen Moment...</p>
          </div>
        </div>

        <!-- Selected Template Details -->
        <div v-if="selectedDraft" class="selected-template-details mb-4">
          <div class="card border-primary">
            <div
              class="card-header bg-primary text-white d-flex justify-content-between align-items-center"
            >
              <h6 class="mb-0">
                <font-awesome-icon icon="file-alt" class="me-2" />
                Ausgewählte Vorlage
              </h6>
              <button
                type="button"
                class="btn btn-sm btn-outline-light"
                @click="clearDraftSelection"
                title="Auswahl entfernen"
              >
                <font-awesome-icon icon="times" />
              </button>
            </div>
            <div class="card-body">
              <!-- Basketball-spezifische Darstellung -->
              <div v-if="isBasketballTemplate(selectedDraft)" class="basketball-selected">
                <div class="basketball-matchup">
                  <div class="team-names">
                    <div class="home-team">{{ getDraftFieldValue(selectedDraft, 'homeTeam') }}</div>
                    <div class="vs">vs.</div>
                    <div class="guest-team">
                      {{ getDraftFieldValue(selectedDraft, 'guestTeam') }}
                    </div>
                  </div>
                  <div class="match-details">
                    <div class="venue" v-if="getDraftFieldValue(selectedDraft, 'gym')">
                      <font-awesome-icon icon="map-marker-alt" class="me-1" />
                      {{ getDraftFieldValue(selectedDraft, 'gym') }}
                    </div>
                    <div class="league-info" v-if="getDraftFieldValue(selectedDraft, 'ligaName')">
                      <font-awesome-icon icon="trophy" class="me-1" />
                      {{ getDraftFieldValue(selectedDraft, 'ligaName') }}
                      <span class="ms-1" v-if="getDraftFieldValue(selectedDraft, 'matchNo')">
                        {{ getDraftFieldValue(selectedDraft, 'matchNo') }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- Standard-Darstellung für andere Vorlagen -->
              <div v-else class="standard-selected">
                <div class="row">
                  <div class="col-md-6">
                    <h6 class="text-muted mb-2">Vorlagendaten:</h6>
                    <div
                      v-for="(fieldData, key) in getDraftPreviewFields(selectedDraft)"
                      :key="key"
                      class="draft-preview-item"
                    >
                      <span class="draft-preview-label">{{ fieldData.label }}:</span>
                      <span class="draft-preview-value">{{ fieldData.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Drafts list -->
        <div
          v-else-if="drafts.length > 0 && !draftsLoading"
          class="drafts-list"
          style="overflow-x: hidden"
        >
          <div class="row g-2">
            <div v-for="draft in drafts" :key="draft.id" class="col-md-6 col-lg-4">
              <div
                class="draft-item"
                :class="{ selected: selectedDraft?.id === draft.id }"
                @click="selectDraft(draft)"
              >
                <!-- Draft header -->
                <div class="draft-item-header">
                  <div class="draft-item-info">
                    <h6 class="mb-1 barlow">
                      <font-awesome-icon
                        icon="calendar"
                        class="me-1"
                        v-if="getDraftDate(draft) !== ''"
                      />
                      {{ getDraftDate(draft) }}
                    </h6>
                  </div>
                </div>

                <!-- Draft preview -->
                <div class="draft-item-preview">
                  <!-- Basketball-spezifische Darstellung -->
                  <div v-if="isBasketballTemplate(draft)" class="basketball-preview">
                    <div class="basketball-matchup">
                      <div class="team-names">
                        <span class="home-team">{{ getDraftFieldValue(draft, 'homeTeam') }}</span>
                        <span class="vs">vs.</span>
                        <span class="guest-team">{{ getDraftFieldValue(draft, 'guestTeam') }}</span>
                      </div>
                      <div class="match-details" v-if="getDraftFieldValue(draft, 'gym')">
                        <div class="venue">
                          <font-awesome-icon icon="map-marker-alt" class="me-1" />
                          {{ getDraftFieldValue(draft, 'gym') }}
                        </div>
                        <div class="league-info" v-if="getDraftFieldValue(draft, 'ligaName')">
                          <font-awesome-icon icon="trophy" class="me-1" />
                          {{ getDraftFieldValue(draft, 'ligaName') }}
                          <span class="ms-1" v-if="getDraftFieldValue(draft, 'matchNo')">
                            {{ getDraftFieldValue(draft, 'matchNo') }}</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Standard-Darstellung für andere Vorlagen -->
                  <div v-else>
                    <div
                      v-for="(fieldData, key) in getDraftPreviewFields(draft)"
                      :key="key"
                      class="draft-preview-item"
                    >
                      <span class="draft-preview-label">{{ fieldData.label }}:</span>
                      <span class="draft-preview-value">{{ fieldData.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- /drafts-list -->

        <!-- No drafts -->
        <div v-if="!draftsLoading && drafts.length === 0" class="text-center py-4">
          <font-awesome-icon icon="file-alt" class="text-muted mb-3" style="font-size: 3rem" />
          <p class="text-muted">Keine Vorlagen gefunden</p>
          <p class="small text-muted">
            Erstelle eine neue Vorlage oder ändere deine Suchkriterien.
          </p>
          <p>
            <button
              class="btn btn-outline-secondary"
              @click="clearDraftFilters"
              v-if="activeFiltersCount > 0"
            >
              <font-awesome-icon icon="times" class="me-1" />
              <span>Filter löschen</span>
            </button>
          </p>
        </div>

        <!-- Pagination -->
        <div
          v-if="!draftsLoading && draftPagination.totalPages > 1 && drafts.length > 0"
          class="d-flex justify-content-center mt-3"
        >
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: draftPagination.currentPage === 1 }">
                <button class="page-link" @click="changeDraftPage(draftPagination.currentPage - 1)">
                  <font-awesome-icon icon="chevron-left" />
                </button>
              </li>

              <li
                v-for="page in getVisibleDraftPages()"
                :key="page"
                class="page-item"
                :class="{ active: page === draftPagination.currentPage }"
              >
                <button class="page-link" @click="changeDraftPage(page)">
                  {{ page }}
                </button>
              </li>

              <li
                class="page-item"
                :class="{
                  disabled: draftPagination.currentPage === draftPagination.totalPages,
                }"
              >
                <button class="page-link" @click="changeDraftPage(draftPagination.currentPage + 1)">
                  <font-awesome-icon icon="chevron-right" />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      <!-- /card-body -->

      <!-- ───── Footer ───── -->
      <div class="card-footer">
        <div class="d-flex justify-content-between">
          <button type="button" class="btn btn-secondary" @click="showDraftSelection = false">
            Abbrechen
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="confirmDraftSelection"
            :disabled="!selectedDraft"
          >
            Vorlage verwenden
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch, onUnmounted, provide, toRaw } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { useFileUploadStore } from '@/stores/fileUpload'

import api from '@/api/api'
import { receiptsApi } from '@/api/receipts'
import { templateService } from '@/api/templates'
import teamTemplateDraftsService from '@/api/teamTemplateDrafts'
import { showSuccess, showError, showConfirmDialog } from '@/services/notification'
import { storeToRefs } from 'pinia'
import AsyncFileUpload from '@/components/AsyncFileUpload.vue'
import { useTeamIntegrityChecks } from '@/composables/useTeamIntegrityChecks'
import { buildCategoryInfo, getCategoryLabel as resolveCategoryLabel, getCategoryIcon as resolveCategoryIcon } from '@/utils/categoryInfo'
import { createReceiptFieldFormatter } from '@/utils/receiptFieldUtils'
import { ReceiptWizardSymbol } from './tokens'
import { useReceiptFormatting } from './composables/useReceiptFormatting'
import WizardHeader from './components/WizardHeader.vue'
import WizardProgress from './components/WizardProgress.vue'
import WizardFooter from './components/WizardFooter.vue'
import StepTeamSelection from './components/steps/StepTeamSelection.vue'
import StepTemplateSelection from './components/steps/StepTemplateSelection.vue'
import StepDraftSelection from './components/steps/StepDraftSelection.vue'
import StepSignature from './components/steps/StepSignature.vue'
import StepCopy from './components/steps/StepCopy.vue'
import StepReview from './components/steps/StepReview.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const teamsStore = useTeamsStore()
const fileUploadStore = useFileUploadStore()
const { loadTeamById, refreshTeams } = teamsStore

const sourceReceiptId = computed(() => route.query.sourceReceiptId || null)
const sourceReceipt = ref(null)
const recreatingFromReceipt = ref(false)

const {
  teams: userTeams,
  loading: teamsLoading,
  pagination: teamsPaginationData,
} = storeToRefs(teamsStore)

const currentUser = computed(() => authStore.user)

const currentStep = ref(1)
const creating = ref(false)
const previewing = ref(false)
const nextStepLoading = ref(false)
const locationLoading = ref(false)
const locationUsed = ref({})
const teamSearchQuery = ref('')
const teamsItemsPerPage = 3

const serverDateIso = ref('')
const serverDateLoading = ref(false)
const serverDateError = ref(null)

const toIsoDateString = (value) => {
  if (!value) return ''

  if (typeof value === 'string') {
    const match = value.match(/\d{4}-\d{2}-\d{2}/)
    if (match) {
      return match[0]
    }
  }

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toISOString().split('T')[0]
}

const fetchServerDate = async () => {
  if (serverDateLoading.value) return

  serverDateError.value = null

  try {
    serverDateLoading.value = true
    const response = await api.get('/health')
    const timestamp = response.data?.timestamp

    const isoDate = toIsoDateString(timestamp)
    if (!isoDate) {
      throw new Error('Ungültiger Zeitstempel vom Server')
    }

    serverDateIso.value = isoDate
  } catch (error) {
    console.error('Fehler beim Abrufen des Server-Datums:', error)
    serverDateError.value = error
  } finally {
    serverDateLoading.value = false
  }
}

const ensureServerDate = async () => {
  if (!serverDateIso.value && !serverDateLoading.value) {
    await fetchServerDate()
  }
}

const serverDateLabel = computed(() => {
  if (!serverDateIso.value) return ''
  const parsedDate = new Date(`${serverDateIso.value}T00:00:00Z`)
  if (Number.isNaN(parsedDate.getTime())) {
    return serverDateIso.value
  }
  return parsedDate.toLocaleDateString('de-DE')
})

let searchTimeout = null

const hasMultipleTemplates = computed(() => {
  return availableTemplates.value && availableTemplates.value.length > 1
})

const isRecreationMode = computed(() => !!sourceReceiptId.value && recreatingFromReceipt.value)

const getStepIndex = (stepName) => {
  return steps.value.findIndex(
    (step) => step.title === stepName || step.description.includes(stepName),
  )
}

const isCurrentStep = (stepName) => {
  if (stepName === 'Team' || stepName === 'Persönlich') {
    return currentStep.value === 1
  }
  if (stepName === 'Template') {
    return hasMultipleTemplates.value && currentStep.value === getStepIndex('Template') + 1
  }
  if (stepName === 'Quittung ausfüllen') {
    return currentStep.value === getStepIndex('Quittung ausfüllen') + 1
  }
  if (stepName === 'Vorlage') {
    return currentStep.value === getStepIndex('Vorlage') + 1
  }
  if (stepName === 'Unterschrift') {
    return currentStep.value === getStepIndex('Unterschrift') + 1
  }
  if (stepName === 'Kopie') {
    return currentStep.value === getStepIndex('Kopie') + 1
  }
  if (stepName === 'Vorschau') {
    return currentStep.value === getStepIndex('Vorschau') + 1
  }
  return false
}
const log = () => {
  const fields = draftFilters.value?.fields || {}
  Object.entries(fields).forEach(([fieldName, selectedValues]) => {
    ;(selectedValues || []).forEach((singleValue) => {})
  })
}

const isCurrentSubStep = (category) => {
  if (!isCurrentStep('Quittung ausfüllen')) return false
  const subStep = subSteps.value.find((step) => step.category === category)
  return subStep && currentSubStep.value === subStep.id
}

const isSubStepCompleted = (category) => {
  const categoryFields = templateFieldsByCategory.value[category] || []

  return categoryFields.every((field) => {
    if (!field.required) return true

    const fieldValue = getFieldData(field.name)
    return (
      fieldValue !== undefined &&
      fieldValue !== null &&
      fieldValue !== '' &&
      (!Array.isArray(fieldValue) || fieldValue.length > 0)
    )
  })
}
const goToStep = (step) => {
  if (currentStep.value >= step) {
    currentStep.value = step
  }
}

const getSubStepCompletionStatus = (category) => {
  const categoryFields = templateFieldsByCategory.value[category] || []
  const requiredFields = categoryFields.filter((field) => field.required)
  const completedFields = requiredFields.filter((field) => {
    const fieldValue = getFieldData(field.name)
    return (
      fieldValue !== undefined &&
      fieldValue !== null &&
      fieldValue !== '' &&
      (!Array.isArray(fieldValue) || fieldValue.length > 0)
    )
  })

  if (requiredFields.length === 0) {
    return 'Keine Pflichtfelder'
  }

  return `${completedFields.length} von ${requiredFields.length} Pflichtfeldern ausgefüllt`
}

const getOverallCompletionPercentage = () => {
  if (!templateFields.value || templateFields.value.length === 0) {
    return 100
  }

  const requiredFields = templateFields.value.filter((field) => field.required)
  if (requiredFields.length === 0) {
    return 100
  }

  const completedFields = requiredFields.filter((field) => {
    const fieldValue = getFieldData(field.name)
    return (
      fieldValue !== undefined &&
      fieldValue !== null &&
      fieldValue !== '' &&
      (!Array.isArray(fieldValue) || fieldValue.length > 0)
    )
  })

  return Math.round((completedFields.length / requiredFields.length) * 100)
}

const getOverallCompletionStatus = () => {
  if (!templateFields.value || templateFields.value.length === 0) {
    return 'Keine Felder verfügbar'
  }

  const requiredFields = templateFields.value.filter((field) => field.required)
  if (requiredFields.length === 0) {
    return 'Alle Felder optional'
  }

  const completedFields = requiredFields.filter((field) => {
    const fieldValue = getFieldData(field.name)
    return (
      fieldValue !== undefined &&
      fieldValue !== null &&
      fieldValue !== '' &&
      (!Array.isArray(fieldValue) || fieldValue.length > 0)
    )
  })

  return `${completedFields.length} von ${requiredFields.length} Pflichtfeldern ausgefüllt`
}

const steps = computed(() => {
  const fillStep = () => ({
    title: 'Quittung ausfüllen',
    displayTitle: documentFillStepTitle.value,
    description: 'Formular-Daten eingeben',
  })

  const baseSteps = !formData.value.teamId
    ? [
        { title: 'Persönlich', description: `Persönliche ${documentLabel.value}` },
        fillStep(),
      ]
    : [
        { title: selectedTeam.value?.name || 'Privat', description: 'Team auswählen' },
        fillStep(),
      ]

  if (hasMultipleTemplates.value) {
    baseSteps.splice(1, 0, { title: 'Template', description: 'Template wählen' })
  }

  if (formData.value.teamId && formData.value.teamTemplateId && hasAvailableDrafts.value) {
    baseSteps.splice(2, 0, { title: 'Vorlage', description: 'Vorlage auswählen' })
  }

  // Nur Signatur-Schritt hinzufügen, wenn needsSignature true ist
  if (needsSignature.value) {
    baseSteps.push({ title: 'Unterschrift', description: 'Signatur' })
  } else {
    // Wenn keine Signatur erforderlich, nur nach Kopie fragen
    baseSteps.push({ title: 'Kopie', description: 'Kopie anfordern' })
  }

  baseSteps.push({ title: 'Vorschau', description: 'Überprüfung' })

  return baseSteps
})

const hasAvailableDrafts = computed(() => {
  return drafts.value.length > 0 || draftPagination.value.totalItems > 0
})

// Bestimmt ob eine Signatur erforderlich ist basierend auf dem ausgewählten Template
const needsSignature = computed(() => {
  if (!selectedTemplate.value) {
    return true // Standard: Signatur erforderlich
  }

  // Prüfe zuerst TeamTemplate-spezifische Einstellung
  if (selectedTemplate.value.needsSignature !== undefined) {
    return selectedTemplate.value.needsSignature
  }

  // Fallback: Template-spezifische Einstellung
  if (selectedTemplate.value.template?.needsSignature !== undefined) {
    return selectedTemplate.value.template.needsSignature
  }

  // Standard: Signatur erforderlich
  return true
})

const selectedTemplateCategoryInfo = computed(() =>
  buildCategoryInfo(selectedTemplate.value?.template || selectedTemplate.value)
)

const getCategoryLabel = (category, source) => {
  if (source) {
    return resolveCategoryLabel(category, source)
  }
  return selectedTemplateCategoryInfo.value[category]?.label || category
}

const getCategoryIcon = (category, source) => {
  if (source) {
    return resolveCategoryIcon(category, source)
  }
  return selectedTemplateCategoryInfo.value[category]?.icon || 'question'
}

const getCategoryDisplayName = (category) => getCategoryLabel(category)

const subSteps = computed(() => {
  const categorySteps = []

  if (templateFields.value && templateFields.value.length > 0) {
    const categoryOrder = ['receipt', 'financial', 'personal']

    categoryOrder.forEach((category) => {
      if (
        templateFieldsByCategory.value[category] &&
        templateFieldsByCategory.value[category].length > 0
      ) {
        categorySteps.push({
          id: categorySteps.length + 1,
          title: getCategoryDisplayName(category),
          description: `${templateFieldsByCategory.value[category].length} Felder`,
          category: category,
          icon: getCategoryIcon(category),
        })
      }
    })
  }
  return categorySteps
})

const currentSubStep = ref(1)

const getFieldData = (fieldName) => {
  if (!formData.value.data || !formData.value.data[fieldName]) {
    return null
  }
  return formData.value.data[fieldName].data
}

const setFieldData = (fieldName, data, label = null) => {
  const existing = formData.value.data[fieldName] || {}
  const templateField = templateFields.value?.find((f) => f.name === fieldName)
  const fieldLabel = label || existing.label || templateField?.displayName || fieldName

  const updated = {
    ...existing,
    label: fieldLabel,
    data,
  }

  if (templateField) {
    if (templateField.type && updated.type !== templateField.type) {
      updated.type = templateField.type
    }
    if (templateField.category && updated.category !== templateField.category) {
      updated.category = templateField.category
    }
    const groupId = templateField.options?.groupId
    if (groupId !== undefined) {
      updated.groupId = groupId || null
    } else if (!Object.prototype.hasOwnProperty.call(updated, 'groupId')) {
      updated.groupId = null
    }
    if (templateField.options?.unit) {
      updated.unit = templateField.options.unit
    }
    if (templateField.options?.pricePerUnit) {
      updated.pricePerUnit = templateField.options.pricePerUnit
    }
  } else if (!Object.prototype.hasOwnProperty.call(updated, 'groupId')) {
    updated.groupId = null
  }

  formData.value.data[fieldName] = updated
}

const getFieldLabel = (fieldName) => {
  if (!formData.value.data || !formData.value.data[fieldName]) {
    return fieldName
  }
  return formData.value.data[fieldName].label
}

const getDateWarningMessage = (fieldName) => {
  if (!serverDateIso.value) {
    return ''
  }

  const value = getFieldData(fieldName)
  if (!value) {
    return ''
  }

  const normalizedValue = toIsoDateString(value)
  if (!normalizedValue) {
    return ''
  }

  if (normalizedValue === serverDateIso.value) {
    return ''
  }

  return `Das ausgewählte Datum weicht vom heutigen Datum (${serverDateLabel.value}) ab.`
}

const clearFieldError = (fieldName, category = '') => {
  const errorKey = category ? `${category}-${fieldName}` : fieldName
  if (errors.value[errorKey]) {
    delete errors.value[errorKey]
  }
}

const fieldData = computed({
  get: () => {
    return new Proxy(
      {},
      {
        get(target, prop) {
          return getFieldData(prop)
        },
        set(target, prop, value) {
          setFieldData(prop, value)
          return true
        },
      },
    )
  },
})

const getDefaultFormData = () => ({
  teamId: null,
  teamTemplateId: null,
  totalAmount: 0,
  totalAmountWithTax: 0,
  recipientEmail: '',
  recipientName: '',
  signature: null,
  data: {},
})

const formData = ref(getDefaultFormData())
const lostTemplate = ref(false)

const resetRecreationMode = async () => {
  await router.replace({ name: 'receipt-create' }) 
    // Zuerst Recreation-Mode zurücksetzen, damit Watcher nicht reagieren
  lostTemplate.value = true
  recreatingFromReceipt.value = false
  sourceReceipt.value = null
  
  // Alle Werte zurücksetzen

  uploadedFiles.value = {}
  selectedTemplate.value = null
  templateFields.value = []
  receiptFields.value = []
  teamData.value = null
  availableTemplates.value = []

  // Schritt erst DANACH auf 1 setzen, um Watcher-Auslösung beim Zurücksetzen zu vermeiden
  //await nextTick()
  currentStep.value = 1
  currentSubStep.value = 1
  formData.value = getDefaultFormData()
}

const uploadedFiles = ref({})
const fileUploadRefs = ref({})
const savedSessionIds = ref({})

const saveSessionId = (fieldName, sessionId) => {
  if (sessionId) {
    savedSessionIds.value[fieldName] = sessionId

    fileUploadStore.addSession(sessionId)
  }
}

const getSessionId = (fieldName) => {
  return savedSessionIds.value[fieldName]
}

const receiptFields = ref([])
const teamData = ref(null)

const personalFields = ref([])
let fieldCounter = 0
const errors = ref({})

const availableTemplates = ref([])
const templatesLoading = ref(false)
const selectedTemplate = ref(null)
const templateFields = ref([])

const isInvoiceTemplate = computed(() => {
  const template = selectedTemplate.value?.template || selectedTemplate.value
  return template?.name === 'invoice'
})

const documentLabel = computed(() => (isInvoiceTemplate.value ? 'Rechnung' : 'Quittung'))
const documentPluralLabel = computed(() => (isInvoiceTemplate.value ? 'Rechnungen' : 'Quittungen'))
const documentSumLabel = computed(() =>
  isInvoiceTemplate.value ? 'Rechnungssumme' : 'Quittungssumme',
)
const documentFillStepTitle = computed(() => `${documentLabel.value} ausfüllen`)

const autoTemplateSelectionInProgress = ref(false)

const getTodayIsoDate = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const originalTemplateFields = ref([])

const drafts = ref([])
const draftsLoading = ref(false)
const selectedDraft = ref(null)
const draftFilters = ref({
  dateFrom: getTodayIsoDate(),
  dateTo: getTodayIsoDate(),
  fields: {},
})
const draftSearchQuery = ref('')
const draftPagination = ref({
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  filteredItems: 0,
  itemsPerPage: 6,
})
const showDraftSelection = ref(false)
const showFieldFilterModal = ref(false)
const filterModalLoading = ref(false)

const availableFilters = ref({
  fieldValues: {},
})

const openMultiselect = ref(null)
const multiselectSearch = ref({})

const signatureCanvas = ref(null)
const isDrawing = ref(false)
const hasSignature = ref(false)
const signatureDataURL = ref('')
const storedSignature = computed(() => authStore.user?.signature || '')
const storedSignatureUpdatedAt = computed(() => authStore.user?.signatureUpdatedAt || null)
const hasStoredSignature = computed(() => !!storedSignature.value)
const usingStoredSignature = ref(false)

const totalSteps = computed(() => {
  return getStepIndex('Vorschau') + 1
})

const selectedTeam = computed(() => {
  if (!formData.value.teamId || !userTeams.value) return null
  return userTeams.value.find((team) => team.id === formData.value.teamId)
})

const { checkMissingRequiredFields, checkRefundDataRequirements } = useTeamIntegrityChecks({
  selectedTeam,
  currentUser,
  loadTeamById,
  refreshTeams,
  persistDismissal: false,
})

const templateFieldsByCategory = computed(() => {
  const fields = templateFields.value || []

  const sortFields = (categoryFields) => {
    return categoryFields
      .filter((field) => field.category === categoryFields[0]?.category)
      .sort((a, b) => {
        const orderA = a.order || 0
        const orderB = b.order || 0
        if (orderA !== orderB) {
          return orderA - orderB
        }

        return (a.displayName || '').localeCompare(b.displayName || '')
      })
  }

  return {
    personal: sortFields(fields.filter((field) => field.category === 'personal')),
    receipt: sortFields(fields.filter((field) => field.category === 'receipt')),
    financial: sortFields(fields.filter((field) => field.category === 'financial')),
  }
})

const fieldGroupsByCategory = computed(() => {
  const settingsGroups =
    selectedTemplate.value?.settings?.fieldGroups ||
    selectedTemplate.value?.template?.settings?.fieldGroups ||
    {}

  const normalize = (category) => {
    const groups = Array.isArray(settingsGroups?.[category]) ? settingsGroups[category] : []
    return groups
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((group) => ({
        id: group.id,
        name: group.name || '',
        color: group.color || '#E9ECEF',
      }))
      .filter((group) => group.id && group.name)
  }

  return {
    personal: normalize('personal'),
    receipt: normalize('receipt'),
    financial: normalize('financial'),
  }
})

const groupedTemplateFields = computed(() => {
  const fields = templateFields.value || []

  const buildCategory = (category) => {
    const groups = fieldGroupsByCategory.value[category] || []
    const groupMap = new Map(
      groups.map((group) => [
        group.id,
        {
          ...group,
          fields: [],
        },
      ]),
    )

    const ungrouped = []

    fields
      .filter((field) => field.category === category)
      .sort((a, b) => {
        const orderA = a.order || 0
        const orderB = b.order || 0
        if (orderA !== orderB) {
          return orderA - orderB
        }
        return (a.displayName || '').localeCompare(b.displayName || '')
      })
      .forEach((field) => {
        const groupId = field.options?.groupId || null
        if (groupId && groupMap.has(groupId)) {
          groupMap.get(groupId)?.fields.push(field)
        } else {
          ungrouped.push(field)
        }
      })

    const populatedGroups = Array.from(groupMap.values()).filter(
      (group) => Array.isArray(group.fields) && group.fields.length > 0,
    )

    return {
      groups: populatedGroups,
      ungrouped,
    }
  }

  return {
    personal: buildCategory('personal'),
    receipt: buildCategory('receipt'),
    financial: buildCategory('financial'),
  }
})

const hexToRgba = (hex, alpha = 0.1) => {
  if (!hex || typeof hex !== 'string') {
    return `rgba(215, 16, 33, ${alpha})`
  }
  let normalized = hex.replace('#', '').trim()
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((char) => char + char)
      .join('')
  }
  if (normalized.length !== 6) {
    return `rgba(215, 16, 33, ${alpha})`
  }
  const intVal = parseInt(normalized, 16)
  const r = (intVal >> 16) & 255
  const g = (intVal >> 8) & 255
  const b = intVal & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const getGroupBlockStyle = (color) => {
  const fallbackColor = color || '#D71021'
  return {
    borderLeft: `6px solid ${fallbackColor}`,
    background: hexToRgba(fallbackColor, 0.08),
    borderRadius: '14px',
  }
}

const filteredTeams = computed(() => {
  return userTeams.value || []
})

const paginatedTeams = computed(() => {
  return filteredTeams.value
})

const totalTeamPages = computed(() => {
  return teamsPaginationData.value?.totalPages || 1
})

const showTeamsPagination = computed(() => {
  return (teamsPaginationData.value?.total || 0) > teamsItemsPerPage
})

const showTeamsSearch = computed(() => {
  return !!teamSearchQuery.value || (teamsPaginationData.value?.total || 0) > 3
})

const canProceed = computed(() => {
  if (currentStep.value === 1) {
    if (isRecreationMode.value) {
      return true
    }
    if (!formData.value.teamId) {
      const personalCoins = authStore.user?.account?.amount || 0
      return personalCoins >= 1
    }
    if (formData.value.teamId) {
      const selectedTeam = userTeams.value?.find((t) => t.id === formData.value.teamId)
      const teamCoins = selectedTeam?.account?.amount || 0
      return teamCoins >= 1
    }
    return true
  }

  if (isCurrentStep('Template')) {
    return !!formData.value.teamTemplateId
  }

  if (currentStep.value === 3 && formData.value.teamId && formData.value.teamTemplateId) {
    return true
  }

  if (isCurrentStep('Quittung ausfüllen')) {
    return validateBasicInfo()
  }

  if (isCurrentStep('Template-Felder')) {
    return validateTemplateFields()
  }

  if (isCurrentStep('Felder') || isCurrentStep('Einstellungen')) {
    if (!formData.value.teamId) {
      return validatePersonalFields()
    } else {
      return validateTeamFields()
    }
  }

  if (isCurrentStep('Unterschrift')) {
    const hasCanvas = !!signatureCanvas.value
    const hasSavedSignature = !!signatureDataURL.value
    const signatureValid = hasSignature.value && (hasCanvas || hasSavedSignature)
    const emailValid = !formData.value.recipientEmail || isValidEmail(formData.value.recipientEmail)
    return signatureValid && emailValid
  }

  if (isCurrentStep('Kopie')) {
    const emailValid = !formData.value.recipientEmail || isValidEmail(formData.value.recipientEmail)
    return emailValid
  }

  if (isCurrentStep('Vorschau')) {
    return true
  }

  return false
})

watch(
  () => formData.value.teamId,
  async (newTeamId) => {
    if (newTeamId) {
      await loadTeamData(newTeamId)

      if (selectedTeam.value && !formData.value.recipientName) {
        formData.value.recipientName = selectedTeam.value.name
      }
    } else {
      receiptFields.value = []
      teamData.value = null
      if (!isRecreationMode.value) {
        formData.value.recipientName = ''
      }

      if (currentStep.value === 1 && !lostTemplate.value) {
        await nextStep()
      }
    }
  },
)

// Watcher für Template-Auswahl
watch(
  () => formData.value.teamTemplateId,
  async (newTemplateId) => {
    if (newTemplateId && isCurrentStep('Template')) {
      if (autoTemplateSelectionInProgress.value) {
        autoTemplateSelectionInProgress.value = false
        return
      }
      // Automatisch zum nächsten Schritt weiterleiten nach Template-Auswahl
      await nextStep()
    }
  },
)

// Watcher für Draft-Auswahl
watch(
  () => selectedDraft.value,
  async (newDraft) => {
    if (newDraft && isCurrentStep('Vorlage')) {
      // Automatisch zum nächsten Schritt weiterleiten nach Draft-Auswahl
      await confirmDraftSelection()
    }
  },
)

// Watcher für manuelle Erstellung
watch(
  () => showDraftSelection.value,
  async (newValue, oldValue) => {
    if (
      oldValue === true &&
      newValue === false &&
      isCurrentStep('Vorlage') &&
      !selectedDraft.value
    ) {
      // Automatisch zum nächsten Schritt weiterleiten nach manueller Erstellung
      await nextStep()
    }
  },
)

const performTeamSearch = async () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  searchTimeout = setTimeout(async () => {
    await teamsStore.fetchTeams(1, teamsItemsPerPage, teamSearchQuery.value)

    if (filteredTeams.value.length === 1) {
      const singleTeam = filteredTeams.value[0]
      const teamCoins = singleTeam?.account?.amount || 0

      if (teamCoins >= 1) {
        selectTeam(singleTeam.id)
      }
    }
  }, 300)
}

watch(teamSearchQuery, performTeamSearch)

watch(currentStep, async (newStep) => {
  if (
    newStep === getStepIndex('Vorlage') + 1 &&
    formData.value.teamId &&
    formData.value.teamTemplateId &&
    drafts.value.length === 0
  ) {
    await loadDrafts()
  }
})

watch(
  () => [draftFilters.value.dateFrom, draftFilters.value.dateTo],
  async () => {
    if (!formData.value.teamTemplateId) return
    draftPagination.value.currentPage = 1

    const shouldShowOverlay = showFieldFilterModal.value
    if (shouldShowOverlay) filterModalLoading.value = true
    try {
      await loadDrafts()
    } finally {
      if (shouldShowOverlay) filterModalLoading.value = false
    }
  },
)

watch(
  () => draftFilters.value.fields,
  async () => {
    if (!formData.value.teamTemplateId) return
    draftPagination.value.currentPage = 1
    const shouldShowOverlay = showFieldFilterModal.value
    if (shouldShowOverlay) filterModalLoading.value = true
    try {
      await loadDrafts()
    } finally {
      if (shouldShowOverlay) filterModalLoading.value = false
    }
  },
  { deep: true },
)

const selectPersonal = async () => {
  const personalCoins = authStore.user?.account?.amount || 0

  if (personalCoins < 1) {
    showError(
      `Dein persönlicher Account hat nicht genügend ${documentPluralLabel.value}. Bitte lade dein Konto auf.`,
    )
    return
  }

  if (isRecreationMode.value) {
    return
  }

  formData.value.teamId = null
  formData.value.recipientName = ''

  if (currentStep.value === 1) {
    await nextStep()
  }
}

const selectTeam = async (teamId) => {
  if (!userTeams.value) return

  const team = userTeams.value.find((t) => t.id === teamId)
  const teamCoins = team?.account?.amount || 0

  if (teamCoins < 1) {
    showError(
      `Der Bereich "${team?.name || 'Unbekannt'}" hat nicht genügend ${documentPluralLabel.value}. Bitte lade das Bereich-Konto auf.`,
    )
    return
  }

  if (isRecreationMode.value) {
    return
  }

  // Reset template-related state so the user can re-select after choosing a team
  formData.value.teamTemplateId = null
  selectedTemplate.value = null
  selectedDraft.value = null
  drafts.value = []

  formData.value.teamId = teamId
  formData.value.recipientName = team?.name || ''

  if (currentStep.value === 1) {
    await nextStep()
  }
}

const goToTeamsPage = async (page) => {
  await teamsStore.fetchTeams(page, teamsItemsPerPage, teamSearchQuery.value)
}

const nextTeamsPage = async () => {
  if (teamsPaginationData.value?.page < totalTeamPages.value) {
    const nextPage = (teamsPaginationData.value?.page || 1) + 1
    await teamsStore.fetchTeams(nextPage, teamsItemsPerPage, teamSearchQuery.value)
  }
}

const prevTeamsPage = async () => {
  if (teamsPaginationData.value?.page > 1) {
    const prevPage = (teamsPaginationData.value?.page || 1) - 1
    await teamsStore.fetchTeams(prevPage, teamsItemsPerPage, teamSearchQuery.value)
  }
}

const loadTeamData = async (teamId) => {
  try {
    const selectedTeam = userTeams.value?.find((t) => t.id === teamId)
    if (selectedTeam && !selectedTeam.fields) {
      await teamsStore.loadTeamById(teamId)
    }
  } catch (error) {
    console.error('Error loading team data:', error)
    if (isRecreationMode.value) {
      throw error
    }
    showError('Fehler beim Laden der Bereich-Daten')
  }
}

const loadAvailableTemplates = async () => {
  try {
    templatesLoading.value = true

    if (isRecreationMode.value && sourceReceipt.value?.teamTemplateId) {
      formData.value.teamTemplateId = sourceReceipt.value.teamTemplateId
    }

    if (formData.value.teamId) {
      const response = await templateService.getTeamTemplates(formData.value.teamId)
      availableTemplates.value = response.data || []
    } else {
      const response = await templateService.getPersonalTemplate()
      availableTemplates.value = response.data ? [response.data] : []
    }

    let fallbackTemplateSelected = false
    
    if (formData.value.teamTemplateId) {
      const templateExists = availableTemplates.value.find(
        (t) => t.id === formData.value.teamTemplateId,
      )
      if (!templateExists) {
        formData.value.teamTemplateId = null
        selectedTemplate.value = null
        // Fallback: Wenn ursprüngliches Template (per ID) nicht mehr existiert, versuche anhand
        // des Template-Systemnamens (name, nicht displayName) ein passendes Template zu finden
        if (isRecreationMode.value && sourceReceipt.value) {
          const resolveTemplateNameFromReceipt = (receipt) => {
            const nestedName = receipt?.template?.template?.name
            const directName = receipt?.template?.name
            const separateField = receipt?.templateName
            const name = (nestedName || directName || separateField || '').toString().trim()
            return name ? name.toLowerCase() : null
          }

          const sourceTemplateName = resolveTemplateNameFromReceipt(sourceReceipt.value)
          if (sourceTemplateName) {
            const normalizeTemplateItemName = (tpl) => {
              const nested = tpl?.template?.name
              const direct = tpl?.name
              const alias = tpl?.templateName
              const name = (nested || direct || alias || '').toString().trim()
              return name ? name.toLowerCase() : null
            }

            const fallbackTemplate = availableTemplates.value.find(
              (tpl) => normalizeTemplateItemName(tpl) === sourceTemplateName,
            )

            if (fallbackTemplate) {
              formData.value.teamTemplateId = fallbackTemplate.id
              selectedTemplate.value = fallbackTemplate
              try {
                await selectTemplate(fallbackTemplate)
                fallbackTemplateSelected = true
              } catch (e) {
                // Falls das Laden der Felder fehlschlägt, bleibt zumindest die Auswahl gesetzt
              }
            }
          }
        }
      }
    }

    if (availableTemplates.value.length > 0 && !fallbackTemplateSelected) {
      if (isRecreationMode.value && formData.value.teamTemplateId) {
        const matchingTemplate = availableTemplates.value.find(
          (t) => t.id === formData.value.teamTemplateId,
        )
        if (matchingTemplate) {
          await selectTemplate(matchingTemplate)
        } else {
          formData.value.teamTemplateId = null
          selectedTemplate.value = null
        }
      } else if (
        isRecreationMode.value &&
        !formData.value.teamId &&
        availableTemplates.value.length === 1
      ) {
        await selectTemplate(availableTemplates.value[0])
      } else if (!formData.value.teamTemplateId) {
        // Keine automatische Template-Auswahl hier - wird im Template-Schritt gemacht
        // Bei mehreren Templates keine automatische Auswahl; der Nutzer wählt manuell im nächsten Schritt
      }
    }

    // Prüfe ob im Recreation-Mode kein Template gefunden wurde
    if (isRecreationMode.value && !formData.value.teamTemplateId && !selectedTemplate.value) {
      showError('Template nicht gefunden')
      await resetRecreationMode()
      return
    }
  } catch (error) {
    console.error('Error loading templates:', error)
    if (isRecreationMode.value) {
      showError(`Erstellen der ${documentLabel.value} nicht möglich!`)
      await resetRecreationMode()
      return
    }
    showError(`Fehler beim Laden der Vorlagen: ${error.response?.data?.error || error.message}`)
  } finally {
    templatesLoading.value = false
  }
}

const selectTemplate = async (template) => {
  if (!template) {
    console.warn('Invalid template passed to selectTemplate:', template)
    return
  }

  const actualTemplate = template.template || template
  const isTeamTemplate = !!template.template

  const templateWasAlreadySelected = formData.value.teamTemplateId === template.id

  if (
    actualTemplate.isProFeature &&
    selectedTeam.value &&
    !(selectedTeam.value.status === 'pro' || selectedTeam.value.status === 'ultimate')
  ) {
    showError('Diese Vorlage ist nur für Pro-Bereiche verfügbar')
    return
  }

  if (!isRecreationMode.value) {
    formData.value.teamTemplateId = template.id
  } else if (!formData.value.teamTemplateId) {
    formData.value.teamTemplateId = template.id
  }

  selectedTemplate.value = template

  if (isTeamTemplate && actualTemplate.fields) {
    templateFields.value = actualTemplate.fields
  } else if (!isTeamTemplate && template.fields) {
    templateFields.value = template.fields
  } else {
    try {
      const templateIdToUse = template.id

      const response = await templateService.getTemplateFields(templateIdToUse)
      templateFields.value = response.data || []
    } catch (err) {
      let errorMessage = 'Fehler beim Laden der Template-Felder'
      if (err.response?.data?.error) {
        errorMessage += ': ' + err.response.data.error
      }

      showError(errorMessage)
      templateFields.value = []

      if (isRecreationMode.value) {
        throw err
      }

      if (err.response?.status === 404 || err.response?.data?.error?.includes('nicht gefunden')) {
        await loadAvailableTemplates()
      }
    }
  }

  originalTemplateFields.value = JSON.parse(JSON.stringify(templateFields.value))

  await ensureServerDate()

  initializeFormDataWithTemplateFields()

  if (
    isCurrentStep('Template') &&
    templateWasAlreadySelected &&
    !autoTemplateSelectionInProgress.value
  ) {
    await nextStep()
  }
}

const loadDrafts = async () => {
  if (!formData.value.teamTemplateId) {
    drafts.value = []
    return
  }

  draftsLoading.value = true

  try {
    const params = {
      page: draftPagination.value.currentPage,
      limit: draftPagination.value.itemsPerPage,
      search: draftSearchQuery.value,
    }

    if (draftFilters.value.dateFrom) {
      params.dateFrom = draftFilters.value.dateFrom
    }

    if (draftFilters.value.dateTo) {
      params.dateTo = draftFilters.value.dateTo
    }

    Object.entries(draftFilters.value.fields).forEach(([fieldName, filterValues]) => {
      if (filterValues && Array.isArray(filterValues) && filterValues.length > 0) {
        params[fieldName] = filterValues.join(',')
      }
    })

    const response = await teamTemplateDraftsService.getTeamTemplateDrafts(
      formData.value.teamTemplateId,
      params,
    )

    drafts.value = response.data.data
    draftPagination.value = response.data.pagination

    if (response.data.availableFilters) {
      availableFilters.value = response.data.availableFilters
    }
  } catch (err) {
    console.error('Error loading drafts:', err)
    showError(err.response?.data?.error || 'Fehler beim Laden der Vorlagen')
  } finally {
    draftsLoading.value = false
  }
}

const selectManualCreation = () => {
  selectedDraft.value = null
  showDraftSelection.value = false
}

const selectDraft = (draft) => {
  selectedDraft.value = draft
}

const clearDraftSelection = () => {
  selectedDraft.value = null
}

const confirmDraftSelection = () => {
  if (selectedDraft.value) {
    loadDraftDataIntoForm(selectedDraft.value)
    showDraftSelection.value = false
    nextStep()
  }
}

const loadDraftDataIntoForm = (draft) => {
  if (!draft.data) return

  Object.keys(draft.data).forEach((key) => {
    const fieldData = draft.data[key]
    if (fieldData && fieldData.data !== undefined) {
      setFieldData(key, fieldData.data)
    } else {
      setFieldData(key, fieldData)
    }
  })
}

const formatDraftValue = (value, fieldType = null) => {
  if (!value || value === '') return '-'

  if (fieldType === 'date') {
    const parsedDate = parseDraftDateValue(value)
    if (parsedDate) {
      return parsedDate.toLocaleDateString('de-DE')
    }
  }

  return String(value)
}

const getDraftPreviewFields = (draft) => {
  if (!draft.data) return {}

  const dataKeys = Object.keys(draft.data)
  if (dataKeys.length === 0) return {}

  const templateFields = draft.teamTemplate?.template?.fields || []
  const fieldTypeMap = new Map()
  templateFields.forEach((field) => {
    fieldTypeMap.set(field.name, field.type)
  })

  const previewFields = dataKeys.slice(0, 3)
  const result = {}

  previewFields.forEach((key) => {
    const fieldData = draft.data[key]
    const fieldType = fieldTypeMap.get(key)

    if (fieldData && fieldData.data !== undefined) {
      const value = fieldData.data
      const formattedValue = formatDraftValue(value, fieldType)
      result[key] = {
        label: fieldData.label || key,
        value: formattedValue,
      }
    } else {
      const formattedValue = formatDraftValue(fieldData, fieldType)
      result[key] = {
        label: key,
        value: formattedValue,
      }
    }
  })

  return result
}

// Prüft ob es sich um eine Basketball-Vorlage handelt
const isBasketballTemplate = (draft) => {
  if (!draft.data) return false

  const hasHomeTeam = draft.data.homeTeam && (draft.data.homeTeam.data || draft.data.homeTeam)
  const hasGuestTeam = draft.data.guestTeam && (draft.data.guestTeam.data || draft.data.guestTeam)

  return hasHomeTeam && hasGuestTeam
}

// Holt den Wert eines spezifischen Feldes aus der Vorlage
const getDraftFieldValue = (draft, fieldName) => {
  if (!draft.data || !draft.data[fieldName]) return ''

  const fieldData = draft.data[fieldName]
  if (fieldData && fieldData.data !== undefined) {
    return fieldData.data || ''
  }
  return fieldData || ''
}

// Formatiert Datum und Zeit für Basketball-Vorlagen
const formatBasketballDateTime = (draft) => {
  const dateValue = getDraftFieldValue(draft, 'datum') || getDraftFieldValue(draft, 'date')
  const timeValue = getDraftFieldValue(draft, 'spielzeit') || getDraftFieldValue(draft, 'time')

  let result = ''

  if (dateValue) {
    try {
      const date = new Date(dateValue)
      if (!isNaN(date.getTime())) {
        result += date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } else {
        result += dateValue
      }
    } catch (e) {
      result += dateValue
    }
  }

  if (timeValue) {
    if (result) result += ' '
    result += timeValue
  }

  return result || 'Datum + Zeit'
}

const extractDraftFieldValue = (fieldData) => {
  if (fieldData && typeof fieldData === 'object' && fieldData.data !== undefined) {
    return fieldData.data
  }

  return fieldData
}

const isLikelyDateFieldName = (name) => {
  if (typeof name !== 'string') return false
  return name.toLowerCase().includes('date') || name.toLowerCase().includes('datum')
}

const parseDraftDateValue = (rawValue) => {
  if (rawValue instanceof Date && !isNaN(rawValue.getTime())) {
    return rawValue
  }

  if (typeof rawValue === 'number') {
    if (rawValue > 1000000000000) {
      const fromMilliseconds = new Date(rawValue)
      if (!isNaN(fromMilliseconds.getTime())) return fromMilliseconds
    }

    const numericString = String(Math.trunc(rawValue))
    if (numericString.length === 8) {
      const year = parseInt(numericString.slice(0, 4), 10)
      const month = parseInt(numericString.slice(4, 6), 10)
      const day = parseInt(numericString.slice(6, 8), 10)

      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const normalized = `${year.toString().padStart(4, '0')}-${String(month).padStart(
          2,
          '0',
        )}-${String(day).padStart(2, '0')}`
        const fromCompactNumber = new Date(normalized)
        if (!isNaN(fromCompactNumber.getTime())) return fromCompactNumber
      }
    }
  }

  if (typeof rawValue !== 'string') return null

  const value = rawValue.trim()
  if (!value) return null

  const directPatterns = [
    /^\d{4}-\d{1,2}-\d{1,2}$/,
    /^\d{4}-\d{1,2}-\d{1,2}[ T]\d{2}:\d{2}/,
    /^\d{4}\/\d{1,2}\/\d{1,2}$/,
    /^\d{4}\.\d{1,2}\.\d{1,2}$/,
  ]

  if (directPatterns.some((pattern) => pattern.test(value))) {
    const parsedDirect = new Date(value)
    if (!isNaN(parsedDirect.getTime())) {
      return parsedDirect
    }
  }

  if (/^\d{8}$/.test(value)) {
    const year = parseInt(value.slice(0, 4), 10)
    const month = parseInt(value.slice(4, 6), 10)
    const day = parseInt(value.slice(6, 8), 10)

    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const normalized = `${year.toString().padStart(4, '0')}-${String(month).padStart(
        2,
        '0',
      )}-${String(day).padStart(2, '0')}`
      const parsedCompact = new Date(normalized)
      if (!isNaN(parsedCompact.getTime())) {
        return parsedCompact
      }
    }
  }

  const europeanMatch = value.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})$/)
  if (europeanMatch) {
    const day = parseInt(europeanMatch[1], 10)
    const month = parseInt(europeanMatch[2], 10)
    let year = parseInt(europeanMatch[3], 10)

    if (year < 100) {
      year += year >= 70 ? 1900 : 2000
    }

    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const normalized = `${year.toString().padStart(4, '0')}-${String(month).padStart(
        2,
        '0',
      )}-${String(day).padStart(2, '0')}`
      const parsedEuropean = new Date(normalized)
      if (!isNaN(parsedEuropean.getTime())) {
        return parsedEuropean
      }
    }
  }

  return null
}

const getDraftDate = (draft) => {
  if (!draft.data) return ''

  const templateFields = draft.teamTemplate?.template?.fields || []
  const preferredDateFieldNames = templateFields
    .filter((field) => field.type === 'date')
    .map((field) => field.name)

  const fallbackTemplateFieldNames = templateFields
    .filter(
      (field) =>
        field.type !== 'date' &&
        (isLikelyDateFieldName(field.name) || isLikelyDateFieldName(field.displayName)),
    )
    .map((field) => field.name)

  const defaultFallbackNames = ['date', 'datum']
  const seenFields = new Set()

  const tryResolveDateFromField = (fieldName) => {
    if (!fieldName || seenFields.has(fieldName)) return null
    seenFields.add(fieldName)

    const fieldData = draft.data[fieldName]
    const value = extractDraftFieldValue(fieldData)
    return parseDraftDateValue(value)
  }

  for (const fieldName of preferredDateFieldNames) {
    const parsedDate = tryResolveDateFromField(fieldName)
    if (parsedDate) {
      return parsedDate.toLocaleDateString('de-DE')
    }
  }

  for (const fieldName of [...fallbackTemplateFieldNames, ...defaultFallbackNames]) {
    const parsedDate = tryResolveDateFromField(fieldName)
    if (parsedDate) {
      return parsedDate.toLocaleDateString('de-DE')
    }
  }

  for (const [fieldName, fieldData] of Object.entries(draft.data)) {
    if (seenFields.has(fieldName)) continue
    const value = extractDraftFieldValue(fieldData)
    const parsedDate = parseDraftDateValue(value)
    if (parsedDate) {
      return parsedDate.toLocaleDateString('de-DE')
    }
  }

  return ''
}

const clearDraftFilters = () => {
  draftFilters.value = {
    dateFrom: '',
    dateTo: '',
    fields: {},
  }
  draftSearchQuery.value = ''
  draftPagination.value.currentPage = 1
  openMultiselect.value = null
  multiselectSearch.value = {}
  loadDrafts()
}

const toggleMultiselect = (fieldName) => {
  if (openMultiselect.value === fieldName) {
    openMultiselect.value = null
  } else {
    openMultiselect.value = fieldName

    if (!draftFilters.value.fields[fieldName]) {
      draftFilters.value.fields[fieldName] = []
    }

    if (!multiselectSearch.value[fieldName]) {
      multiselectSearch.value[fieldName] = ''
    }
  }
}

const getFilteredOptions = (fieldName) => {
  const options = getFieldFilterOptions(fieldName)
  const searchTerm = multiselectSearch.value[fieldName] || ''

  if (!searchTerm) return options

  return options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
}

const toggleOption = (fieldName, value) => {
  if (!draftFilters.value.fields[fieldName]) {
    draftFilters.value.fields[fieldName] = []
  }

  const index = draftFilters.value.fields[fieldName].indexOf(value)
  if (index > -1) {
    draftFilters.value.fields[fieldName].splice(index, 1)
  } else {
    draftFilters.value.fields[fieldName].push(value)
  }
}

const isOptionSelected = (fieldName, value) => {
  return (
    draftFilters.value.fields[fieldName] && draftFilters.value.fields[fieldName].includes(value)
  )
}

const clearFieldOptions = (fieldName) => {
  draftFilters.value.fields[fieldName] = []
  multiselectSearch.value[fieldName] = ''
}

const getOptionLabel = (fieldName, value) => {
  const fieldInfo = availableFilters.value.fieldValues[fieldName]
  if (fieldInfo && fieldInfo.type) {
    const type = fieldInfo.type
    if (type === 'money' || type === 'number') {
      return formatAmount(value)
    }
    if (type === 'date') {
      return formatDate(value)
    }
    if (type === 'checkbox') {
      return value === true || value === 'true' ? 'Ja' : 'Nein'
    }
  }
  return String(value)
}

const removeOption = (fieldName, value) => {
  if (draftFilters.value.fields[fieldName]) {
    const index = draftFilters.value.fields[fieldName].indexOf(value)
    if (index > -1) {
      draftFilters.value.fields[fieldName].splice(index, 1)
    }
  }
}

const handleClickOutside = (event) => {
  if (openMultiselect.value && !event.target.closest('.multiselect-container')) {
    openMultiselect.value = null
  }
}

const changeDraftPage = (page) => {
  if (page >= 1 && page <= draftPagination.value.totalPages) {
    draftPagination.value.currentPage = page
    loadDrafts()
  }
}

const getVisibleDraftPages = () => {
  const pages = []
  const current = draftPagination.value.currentPage
  const total = draftPagination.value.totalPages

  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
}

const debouncedDraftSearch = () => {
  clearTimeout(draftSearchTimeout)
  draftSearchTimeout = setTimeout(() => {
    draftPagination.value.currentPage = 1
    loadDrafts()
  }, 1500)
}

let draftSearchTimeout = null

const activeFiltersCount = computed(() => {
  let count = 0
  if (draftFilters.value.dateFrom) count++
  if (draftFilters.value.dateTo) count++
  count += Object.values(draftFilters.value.fields).reduce((acc, v) => {
    if (v && Array.isArray(v)) return acc + v.length
    return acc
  }, 0)
  return count
})

const activeFieldFilterMap = computed(() => {
  const result = {}
  const fields = draftFilters.value?.fields || {}
  Object.entries(fields).forEach(([fieldName, selectedValues]) => {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) return
    const entries = []
    selectedValues.forEach((singleValue) => {
      entries.push({
        fieldName,
        value: singleValue,
        label: getOptionLabel(fieldName, singleValue),
      })
    })
    if (entries.length > 0) {
      result[fieldName] = entries
    }
  })
  return result
})

const activeFieldFilterList = computed(() => {
  const list = []
  const fields = draftFilters.value?.fields || {}
  Object.entries(fields).forEach(([fieldName, selectedValues]) => {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) return
    selectedValues.forEach((singleValue) => {
      list.push({
        fieldName,
        value: singleValue,
        label: getOptionLabel(fieldName, singleValue),
      })
    })
  })
  return list
})

const getFieldDisplayName = (fieldName) => {
  if (
    !selectedTemplate.value ||
    !selectedTemplate.value.template ||
    !selectedTemplate.value.template.fields
  ) {
    return fieldName
  }

  const field = selectedTemplate.value.template.fields.find((f) => f.name === fieldName)
  return field ? field.displayName || field.name : fieldName
}

const resolveFieldDefaultValue = (field) => {
  if (!field || field.defaultValue === undefined || field.defaultValue === null) {
    return undefined
  }

  const rawDefault = field.defaultValue

  switch (field.type) {
    case 'checkbox': {
      if (typeof rawDefault === 'boolean') return rawDefault
      if (typeof rawDefault === 'string') {
        const normalized = rawDefault.trim().toLowerCase()
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true
        if (['false', '0', 'no', 'off'].includes(normalized)) return false
      }
      if (rawDefault === 1) return true
      if (rawDefault === 0) return false
      return Boolean(rawDefault)
    }
    case 'number':
    case 'money': {
      if (rawDefault === '') return ''
      const numericValue = Number(rawDefault)
      return Number.isNaN(numericValue) ? '' : numericValue
    }
    case 'repeater': {
      if (Array.isArray(rawDefault)) return rawDefault
      if (typeof rawDefault === 'string') {
        try {
          const parsed = JSON.parse(rawDefault)
          return Array.isArray(parsed) ? parsed : []
        } catch (err) {
          return []
        }
      }
      return []
    }
    default:
      return rawDefault
  }
}

const initializeFormDataWithTemplateFields = () => {
  if (!templateFields.value || templateFields.value.length === 0) {
    formData.value.data = {}
    return
  }

  const existingData = isRecreationMode.value ? { ...formData.value.data } : {}
  const initializedData = {}

  templateFields.value.forEach((field) => {
    if (!field || !field.name) {
      console.warn('Invalid field object:', field)
      return
    }

    let fieldValue = existingData[field.name]?.data
    const isPersonalField = field.category === 'personal'

    if (fieldValue === undefined) {
      const defaultValue = resolveFieldDefaultValue(field)
      if (defaultValue !== undefined) {
        fieldValue = defaultValue
      }
    }

    if (isRecreationMode.value && isPersonalField && !isInvoiceTemplate.value) {
      fieldValue = ''
      if (uploadedFiles.value[field.name]) {
        delete uploadedFiles.value[field.name]
      }
    }

    switch (field.type) {
      case 'text':
      case 'textarea':
      case 'email':
      case 'tel':
      case 'location':
        if (fieldValue === undefined) {
          fieldValue = ''
        }
        break

      case 'number':
      case 'money':
        if (fieldValue === undefined) {
          fieldValue = ''
        }
        break

      case 'date':
        if (!fieldValue) {
          fieldValue = serverDateIso.value || ''
        }
        break

      case 'select':
        if (fieldValue === undefined) {
          fieldValue = ''
        }
        break

      case 'checkbox':
        if (fieldValue === undefined) {
          fieldValue = false
        }
        break

      case 'file':
        if (fieldValue === undefined) {
          fieldValue = null
        }
        break

      case 'repeater':
        if (fieldValue === undefined) {
          fieldValue = []
        }
        // Initialize repeater data
        if (Array.isArray(fieldValue)) {
          repeaterData.value[field.name] = fieldValue
        } else {
          repeaterData.value[field.name] = []
        }
        break

      default:
        if (fieldValue === undefined) {
          fieldValue = ''
        }
    }

    if (field.name === 'issuer_name' && (!fieldValue || fieldValue === '')) {
      const user = authStore.user
      if (user && user.firstName && user.lastName) {
        fieldValue = `${user.firstName} ${user.lastName}`
      }
    }

    initializedData[field.name] = {
      label: field.displayName || field.name,
      type: field.type,
      data: fieldValue,
      category: field.category,
    }

    if (field.options?.includeTaxRate) {
      let taxRateValue = existingData[`${field.name}_taxRate`]?.data || ''
      if (isRecreationMode.value && isPersonalField) {
        taxRateValue = ''
      }

      const availableTaxRates = Array.isArray(field.options?.availableTaxRates)
        ? field.options.availableTaxRates
        : []
      const defaultTaxRateId =
        field.options?.selectedTaxRateId &&
        (!availableTaxRates.length || availableTaxRates.includes(field.options.selectedTaxRateId))
          ? field.options.selectedTaxRateId
          : ''

      if (field.options?.taxRateSelectionMode === 'user_choice') {
        if (!isRecreationMode.value) {
          taxRateValue = taxRateValue || defaultTaxRateId
        }
      } else if (taxRateValue === '') {
        if (field.options?.selectedTaxRate) {
          taxRateValue = field.options.selectedTaxRate.id || field.options.selectedTaxRateId
        } else if (field.options?.selectedTaxRateId) {
          taxRateValue = field.options.selectedTaxRateId
        }
      }

      initializedData[`${field.name}_taxRate`] = {
        label: `${field.displayName || field.name} Steuersatz`,
        data: taxRateValue,
        category: field.category,
      }
    }
  })

  formData.value.data = initializedData
}

const getTemplateCategories = (template) => {
  const fields = template.template?.fields || template.fields
  if (!fields) return []

  const categories = new Set()
  fields.forEach((field) => {
    if (field.category) {
      categories.add(field.category)
    }
  })

  return Array.from(categories)
}

const validateBasicInfo = () => {
  errors.value = {}
  let isValid = true

  if (!formData.value.teamTemplateId) {
    errors.value.template = 'Template muss ausgewählt werden'
    isValid = false
  }

  if (!templateFields.value || templateFields.value.length === 0) {
    errors.value.templateFields = 'Template hat keine konfigurierten Felder'
    isValid = false
  }

  return isValid
}

const validateTemplateFields = () => {
  errors.value = {}
  let isValid = true

  templateFields.value.forEach((field) => {
    if (field.required) {
      const fieldValue = getFieldData(field.name)

      if (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim())) {
        errors.value[`template-${field.name}`] = `${field.displayName} ist erforderlich`
        isValid = false
      }
    }

    if (field.options?.includeTaxRate && field.options?.taxRateSelectionMode === 'user_choice') {
      const taxRateValue = getFieldData(`${field.name}_taxRate`)
      if (!taxRateValue || taxRateValue === '') {
        errors.value[`template-${field.name}_taxRate`] =
          `Steuersatz für ${field.displayName} ist erforderlich`
        isValid = false
      }
    }
  })

  return isValid
}

const formatValidationAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return value
  }

  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) {
    return value
  }

  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue)
}

const validateCategoryFields = (category) => {
  errors.value = {}
  let isValid = true

  const categoryFields = templateFieldsByCategory.value[category] || []

  categoryFields.forEach((field) => {
    if (field.required) {
      if (field.type === 'file') {
        if (
          !formData.value.teamId ||
          (selectedTeam.value &&
            (selectedTeam.value.status === 'pro' || selectedTeam.value.status === 'ultimate'))
        ) {
          const files = uploadedFiles.value[field.name] || []
          if (files.length === 0) {
            errors.value[`${category}-${field.name}`] = `${field.displayName} ist erforderlich`
            isValid = false
          }
        } else {
          errors.value[`${category}-${field.name}`] =
            `${field.displayName} ist nur für Pro- und Ultimate-Teams verfügbar`
          isValid = false
        }
      } else {
        const fieldValue = getFieldData(field.name)
        if (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim())) {
          errors.value[`${category}-${field.name}`] = `${field.displayName} ist erforderlich`
          isValid = false
        }
      }
    }

    if (field.options?.includeTaxRate && field.options?.taxRateSelectionMode === 'user_choice') {
      const taxRateValue = getFieldData(`${field.name}_taxRate`)
      if (!taxRateValue || taxRateValue === '') {
        errors.value[`${category}-${field.name}_taxRate`] =
          `Steuersatz für ${field.displayName} ist erforderlich`
        isValid = false
      }
    }

    // Min/Max validation for financial fields
    if (category === 'financial' && field.validation) {
      const fieldValue = getFieldData(field.name)
      if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
        const numericValue = parseFloat(fieldValue)
        if (!isNaN(numericValue)) {
          if (
            field.validation.min !== null &&
            field.validation.min !== undefined &&
            numericValue < field.validation.min
          ) {
            errors.value[`${category}-${field.name}`] =
              `${field.displayName} muss mindestens ${formatValidationAmount(field.validation.min)} betragen`
            isValid = false
          }
          if (
            field.validation.max !== null &&
            field.validation.max !== undefined &&
            numericValue > field.validation.max
          ) {
            errors.value[`${category}-${field.name}`] =
              `${field.displayName} darf höchstens ${formatValidationAmount(field.validation.max)} betragen`
            isValid = false
          }
        }
      }
    }

    // Repeater field validation
    if (field.type === 'repeater') {
      const repeaterItems = getRepeaterItems(field.name)

      // Check minimum items
      if (
        field.options?.repeaterMinItems &&
        repeaterItems.length < field.options.repeaterMinItems
      ) {
        errors.value[`${category}-${field.name}`] =
          `${field.displayName} muss mindestens ${field.options.repeaterMinItems} Positionen haben`
        isValid = false
      }

      // Check maximum items
      if (
        field.options?.repeaterMaxItems &&
        repeaterItems.length > field.options.repeaterMaxItems
      ) {
        errors.value[`${category}-${field.name}`] =
          `${field.displayName} darf höchstens ${field.options.repeaterMaxItems} Positionen haben`
        isValid = false
      }

      // Validate each repeater item
      repeaterItems.forEach((item, index) => {
        // Validate quantity
        if (!item.quantity || item.quantity <= 0) {
          errors.value[`${field.name}-${index}-quantity`] = 'Anzahl muss größer als 0 sein'
          isValid = false
        }

        // Validate description
        if (!item.description || !item.description.trim()) {
          errors.value[`${field.name}-${index}-description`] = 'Beschreibung ist erforderlich'
          isValid = false
        }

        // Validate unit price
        if (!item.unitPrice || item.unitPrice < 0) {
          errors.value[`${field.name}-${index}-unitPrice`] =
            'Einzelpreis muss größer oder gleich 0 sein'
          isValid = false
        }

        // Validate tax rate if required
        if (
          field.options?.repeaterIncludeTaxRate &&
          field.options?.repeaterTaxRateSelectionMode === 'user_choice'
        ) {
          if (!item.taxRateId) {
            errors.value[`${field.name}-${index}-taxRateId`] = 'Steuersatz ist erforderlich'
            isValid = false
          }
        }
      })
    }
  })

  return isValid
}

const validateTeamFields = () => {
  errors.value = {}
  let isValid = true

  receiptFields.value.forEach((field) => {
    if (field.required) {
      if (field.type === 'file') {
        if (
          selectedTeam.value &&
          (selectedTeam.value.status === 'pro' || selectedTeam.value.status === 'ultimate')
        ) {
          const files = uploadedFiles.value[field.name] || []

          if (files.length === 0) {
            errors.value[`receipt-${field.name}`] = `${field.name} ist erforderlich`
            isValid = false
          }
        }
      } else {
        if (!getFieldData(field.name)) {
          errors.value[`receipt-${field.name}`] = `${field.name} ist erforderlich`
          isValid = false
        }
      }
    }
  })

  return isValid
}

const handleFileValidationChange = (fieldName, isValid) => {
  if (isValid) {
    delete errors.value[`receipt-${fieldName}`]
  } else {
    errors.value[`receipt-${fieldName}`] = `${fieldName} ist erforderlich`
  }
}

watch(
  uploadedFiles,
  (newUploadedFiles) => {
    for (const [fieldName, files] of Object.entries(newUploadedFiles)) {
      if (Array.isArray(files) && files.length > 0) {
        setFieldData(fieldName, files)
      } else {
        delete formData.value.data[fieldName]
      }
    }
  },
  { deep: true },
)

watch(
  () => formData.value.data?.buyer_email?.data,
  (newBuyerEmail) => {
    if (newBuyerEmail === undefined) {
      return
    }
    const normalizedEmail = newBuyerEmail == null ? '' : newBuyerEmail
    if (normalizedEmail !== formData.value.recipientEmail) {
      formData.value.recipientEmail = normalizedEmail
    }
  },
)

watch(
  () => formData.value.data,
  () => {
    calculateTotalAmount()
  },
  { deep: true },
)

watch(
  () => {
    const taxRateSelections = {}
    if (templateFields.value) {
      templateFields.value.forEach((field) => {
        if (
          field.options?.includeTaxRate &&
          field.options?.taxRateSelectionMode === 'user_choice'
        ) {
          taxRateSelections[field.name] = getFieldData(`${field.name}_taxRate`)
        }
      })
    }
    return taxRateSelections
  },
  () => {
    calculateTotalAmount()
  },
  { deep: true },
)

const validatePersonalFields = () => {
  errors.value = {}
  let isValid = true

  return isValid
}

const validateRecipientEmail = () => {
  if (!formData.value.recipientEmail?.trim()) {
    delete errors.value.recipientEmail
    return
  }
  if (!isValidEmail(formData.value.recipientEmail)) {
    errors.value.recipientEmail = 'Bitte gib eine gültige E-Mail-Adresse ein'
  } else {
    delete errors.value.recipientEmail
  }
}

const getNextButtonTooltip = () => {
  if (currentStep.value === 1) {
    if (!formData.value.teamId) {
      const personalCoins = authStore.user?.account?.amount || 0
      if (personalCoins < 1) {
        return `Nicht genügend persönliche ${documentPluralLabel.value} verfügbar. Bitte lade dein Konto auf.`
      }
    } else {
      const selectedTeam = userTeams.value?.find((t) => t.id === formData.value.teamId)
      const teamCoins = selectedTeam?.account?.amount || 0
      if (teamCoins < 1) {
        return `Der Bereich "${selectedTeam?.name || 'Unbekannt'}" hat nicht genügend ${documentPluralLabel.value}. Bitte lade das Bereich-Konto auf.`
      }
    }
  }
  return ''
}

const nextButtonTooltip = computed(() => getNextButtonTooltip())

const handleNextStep = async () => {
  if (currentStep.value === 1) {
    if (!isRecreationMode.value) {
      if (!formData.value.teamId) {
        const personalCoins = authStore.user?.account?.amount || 0
        if (personalCoins < 1) {
          showError(
            `Dein persönlicher Account hat nicht genügend ${documentPluralLabel.value}. Bitte lade dein Konto auf.`,
          )
          return
        }
      } else {
        const selectedTeam = userTeams.value?.find((t) => t.id === formData.value.teamId)
        const teamCoins = selectedTeam?.account?.amount || 0
        if (teamCoins < 1) {
          showError(
            `Der Bereich "${selectedTeam?.name || 'Unbekannt'}" hat nicht genügend ${documentPluralLabel.value}. Bitte lade das Bereich-Konto auf.`,
          )
          return
        }

        const refundDataValid = await checkRefundDataRequirements()
        if (!refundDataValid) {
          return
        }

        const canProceedCheck = await checkMissingRequiredFields()
        if (!canProceedCheck) {
          showError(
            `Pflichtfelder müssen ausgefüllt werden, um Bereich-${documentPluralLabel.value} zu erstellen.`,
          )
          return
        }
      }
    }
  }

  if (currentStep.value === 3 && formData.value.teamId && formData.value.teamTemplateId) {
  }

  if (isCurrentStep('Quittung ausfüllen')) {
    let isValid = validateCategoryFields('receipt')
    if (!isValid) {
      currentSubStep.value = subSteps.value.findIndex((step) => step.category === 'receipt') + 1
      return
    }
    isValid = validateCategoryFields('financial')
    if (!isValid) {
      currentSubStep.value = subSteps.value.findIndex((step) => step.category === 'financial') + 1
      return
    }
    isValid = validateCategoryFields('personal')
    if (!isValid) {
      currentSubStep.value = subSteps.value.findIndex((step) => step.category === 'personal') + 1
      return
    }
  }

  if (isCurrentStep('Unterschrift')) {
    validateRecipientEmail()
    if (errors.value.recipientEmail) {
      return
    }
  }
  nextStepLoading.value = true

  try {
    await nextStep()
  } finally {
    nextStepLoading.value = false
  }
}

const nextStep = async () => {
  if (isCurrentStep('Felder') && formData.value.teamId) {
    validateTeamFields()
  }

  if (canProceed.value) {
    if (currentStep.value === 1) {
      await loadAvailableTemplates()

      if (hasMultipleTemplates.value) {
        currentStep.value++
      } else {
        if (availableTemplates.value.length > 0) {
          await selectTemplate(availableTemplates.value[0])

          if (formData.value.teamId && formData.value.teamTemplateId) {
            if (drafts.value.length === 0) {
              await loadDrafts()
            }

            if (hasAvailableDrafts.value) {
              currentStep.value = getStepIndex('Vorlage') + 1
            } else {
              selectedDraft.value = null
              currentStep.value = getStepIndex('Quittung ausfüllen') + 1
              initializeSubStep()
            }
          } else {
            currentStep.value = getStepIndex('Quittung ausfüllen') + 1
            initializeSubStep()
          }
        }
      }
    } else if (isCurrentStep('Template')) {
      if (formData.value.teamId && formData.value.teamTemplateId) {
        if (drafts.value.length === 0) {
          await loadDrafts()
        }

        if (hasAvailableDrafts.value) {
          currentStep.value++
        } else {
          selectedDraft.value = null
          currentStep.value = getStepIndex('Quittung ausfüllen') + 1
          initializeSubStep()
        }
      } else {
        currentStep.value = getStepIndex('Quittung ausfüllen') + 1
        initializeSubStep()
      }
    } else if (isCurrentStep('Vorlage') && formData.value.teamId && formData.value.teamTemplateId) {
      currentStep.value = getStepIndex('Quittung ausfüllen') + 1
      initializeSubStep()
    } else if (isCurrentStep('Quittung ausfüllen')) {
      // Prüfe ob Signatur erforderlich ist
      if (needsSignature.value) {
        currentStep.value = getStepIndex('Unterschrift') + 1
      } else {
        currentStep.value = getStepIndex('Kopie') + 1
      }
    } else {
      currentStep.value++
    }
  }
}

const initializeSubStep = () => {
  if (subSteps.value.length > 0) {
    currentSubStep.value = subSteps.value[0].id
  }
}

const nextSubStep = () => {
  if (currentSubStep.value < subSteps.value.length) {
    currentSubStep.value++
  }
}

const previousSubStep = () => {
  if (currentSubStep.value > 1) {
    currentSubStep.value--
  }
}

const previousStep = () => {
  if (isCurrentStep('Unterschrift')) {
    currentStep.value = getStepIndex('Quittung ausfüllen') + 1

    if (subSteps.value.length > 0) {
      currentSubStep.value = subSteps.value[subSteps.value.length - 1].id
    }
  } else if (isCurrentStep('Kopie')) {
    currentStep.value = getStepIndex('Quittung ausfüllen') + 1

    if (subSteps.value.length > 0) {
      currentSubStep.value = subSteps.value[subSteps.value.length - 1].id
    }
  } else if (isCurrentStep('Quittung ausfüllen')) {
    if (currentSubStep.value > 1) {
      currentSubStep.value--
    } else {
      if (formData.value.teamId && formData.value.teamTemplateId && hasAvailableDrafts.value) {
        currentStep.value = getStepIndex('Vorlage') + 1
      } else if (hasMultipleTemplates.value) {
        currentStep.value = 2
      } else {
        currentStep.value = 1
      }
    }
  } else if (isCurrentStep('Vorlage') && formData.value.teamId && formData.value.teamTemplateId) {
    currentStep.value = 2
  } else {
    currentStep.value--
  }
}

const getCanvasCoordinates = (canvas, e) => {
  const rect = canvas.getBoundingClientRect()
  const scaleX = canvas.width / rect.width
  const scaleY = canvas.height / rect.height

  let clientX, clientY
  if (e.type.includes('touch')) {
    clientX = e.touches[0].clientX
    clientY = e.touches[0].clientY
  } else {
    clientX = e.clientX
    clientY = e.clientY
  }

  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  }
}

const startDrawing = (e) => {
  if (!signatureCanvas.value) return

  e.preventDefault()
  isDrawing.value = true
  usingStoredSignature.value = false

  const canvas = signatureCanvas.value
  const ctx = canvas.getContext('2d')
  const coords = getCanvasCoordinates(canvas, e)

  ctx.beginPath()
  ctx.moveTo(coords.x, coords.y)
}

const draw = (e) => {
  if (!isDrawing.value || !signatureCanvas.value) return

  e.preventDefault()
  usingStoredSignature.value = false
  const canvas = signatureCanvas.value
  const ctx = canvas.getContext('2d')
  const coords = getCanvasCoordinates(canvas, e)

  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#000'
  ctx.lineTo(coords.x, coords.y)
  ctx.stroke()

  hasSignature.value = true

  saveCanvasContent()
}

const stopDrawing = (e) => {
  e.preventDefault()
  isDrawing.value = false
}

const clearSignature = () => {
  if (!signatureCanvas.value) return

  const canvas = signatureCanvas.value
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  hasSignature.value = false
  signatureDataURL.value = ''
  usingStoredSignature.value = false
}

const saveCanvasContent = () => {
  if (signatureCanvas.value) {
    try {
      signatureDataURL.value = signatureCanvas.value.toDataURL()
    } catch (error) {
      console.error('Error saving canvas content:', error)
    }
  }
}

const applyStoredSignature = async () => {
  if (!storedSignature.value) {
    return
  }

  signatureDataURL.value = storedSignature.value
  hasSignature.value = true
  usingStoredSignature.value = true

  await nextTick()
  if (signatureCanvas.value) {
    initializeCanvas()
    restoreCanvasContent()
  }
}

const restoreCanvasContent = () => {
  if (!signatureCanvas.value || !signatureDataURL.value) return

  const canvas = signatureCanvas.value
  const ctx = canvas.getContext('2d')
  const img = new Image()

  img.onload = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0)
  }

  img.src = signatureDataURL.value
}

const initializeCanvas = () => {
  if (!signatureCanvas.value) {
    return
  }
  const canvas = signatureCanvas.value
  const ctx = canvas.getContext('2d')

  canvas.width = 600
  canvas.height = 300

  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = '#000'
  ctx.lineWidth = 2
}

// Funktion zur Normalisierung der _taxRate Felder
const normalizeTaxRateFields = (data) => {
  // Erstelle eine tiefe Kopie des Objekts, die mit Vue 3 Proxy-Objekten funktioniert
  let normalizedData
  try {
    normalizedData = structuredClone(data)
  } catch (error) {
    // Fallback für ältere Browser
    normalizedData = JSON.parse(JSON.stringify(toRaw(data)))
  }

  Object.keys(normalizedData).forEach((key) => {
    if (
      key.endsWith('_taxRate') &&
      normalizedData[key] &&
      typeof normalizedData[key] === 'object'
    ) {
      const fieldData = normalizedData[key]
      if (fieldData.data && typeof fieldData.data === 'object' && fieldData.data.id) {
        // Nur die ID des TaxRate-Objekts speichern
        fieldData.data = fieldData.data.id
      }
    }
  })

  return normalizedData
}

const buildReceiptPayload = async () => {
  const payloadData = normalizeTaxRateFields(formData.value.data)
  const isTeamReceipt = !!formData.value.teamId
  const teamTemplateId = isTeamReceipt ? formData.value.teamTemplateId || undefined : undefined

  const receiptData = {
    totalAmount: parseFloat(formData.value.totalAmount),
    teamId: formData.value.teamId || undefined,
    teamTemplateId,
    recipientEmail: formData.value.recipientEmail || undefined,
    data: payloadData,
  }

  if (needsSignature.value && hasSignature.value) {
    try {
      if (signatureDataURL.value) {
        receiptData.signature = signatureDataURL.value
      } else if (signatureCanvas.value) {
        receiptData.signature = signatureCanvas.value.toDataURL()
      } else {
        throw new Error('No signature data available')
      }
    } catch (canvasError) {
      const error = new Error('SIGNATURE_PROCESSING_FAILED')
      error.type = 'SIGNATURE_PROCESSING_FAILED'
      error.cause = canvasError
      throw error
    }
  }

  return receiptData
}

const createReceipt = async () => {
  creating.value = true

  try {
    const receiptData = await buildReceiptPayload()

    const uploadSessions = {}
    Object.keys(uploadedFiles.value).forEach((fieldName) => {
      const files = uploadedFiles.value[fieldName] || []
      if (files.length > 0) {
        const fileUploadRef = fileUploadRefs.value[fieldName]
        let sessionId = null

        if (fileUploadRef) {
          sessionId = fileUploadRef.getSessionId()
        }
        if (!sessionId) {
          sessionId = getSessionId(fieldName)
        }

        if (sessionId) {
          uploadSessions[fieldName] = sessionId
          if (receiptData.data[fieldName]) {
            delete receiptData.data[fieldName]
          }
        }
      }
    })

    const receiptPayload = {
      ...receiptData,
      uploadSessions: Object.keys(uploadSessions).length > 0 ? uploadSessions : undefined,
    }

    const receipt = await receiptsApi.createReceipt(receiptPayload)

    showSuccess(`${documentLabel.value} erfolgreich erstellt!`)
    router.push(`/receipts/${receipt.id}`)
  } catch (error) {
    if (error?.type === 'SIGNATURE_PROCESSING_FAILED') {
      showError('Fehler beim Verarbeiten der Unterschrift. Bitte unterschreibe erneut.')
    } else {
      console.error('Error creating receipt:', error)
      showError(error.response?.data?.error || `Fehler beim Erstellen der ${documentLabel.value}`)
    }
  } finally {
    creating.value = false
  }
}

const previewReceipt = async () => {
  if (previewing.value) {
    return
  }

  previewing.value = true

  try {
    const receiptData = await buildReceiptPayload()
    const pdfData = await receiptsApi.generateReceiptPreview(receiptData)
    const blob =
      pdfData instanceof Blob
        ? pdfData
        : new Blob([pdfData], { type: 'application/pdf' })

    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000)
  } catch (error) {
    if (error?.type === 'SIGNATURE_PROCESSING_FAILED') {
      showError('Fehler beim Verarbeiten der Unterschrift. Bitte unterschreibe erneut.')
    } else if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text()
        const parsed = JSON.parse(text)
        showError(
          parsed.error ||
            'Fehler beim Erstellen der Vorschau. Bitte überprüfe deine Eingaben.',
        )
      } catch (parseError) {
        console.error('Error parsing preview error response:', parseError)
        showError('Fehler beim Erstellen der Vorschau. Bitte überprüfe deine Eingaben.')
      }
    } else {
      console.error('Error generating preview:', error)
      showError(
        error?.response?.data?.error ||
          'Fehler beim Erstellen der Vorschau. Bitte überprüfe deine Eingaben.',
      )
    }
  } finally {
    previewing.value = false
  }
}

const { formatDate, formatDateTime, formatAmount, formatCurrency } = useReceiptFormatting()

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const getFileCount = (fieldName) => {
  const actualFieldName =
    Object.keys(formData.value.data).find((key) => {
      const fieldData = formData.value.data[key]
      return fieldData && typeof fieldData === 'object' && fieldData.label === fieldName
    }) || fieldName

  return uploadedFiles.value[actualFieldName] ? uploadedFiles.value[actualFieldName].length : 0
}

const isFileField = (fieldName) => {
  const actualFieldName =
    Object.keys(formData.value.data).find((key) => {
      const fieldData = formData.value.data[key]
      return fieldData && typeof fieldData === 'object' && fieldData.label === fieldName
    }) || fieldName

  const templateField = templateFields.value.find((f) => f.name === actualFieldName)
  if (templateField && templateField.type === 'file') {
    return true
  }

  const field = receiptFields.value.find((f) => f.name === actualFieldName)
  return field && field.type === 'file'
}

const getFileIcon = (fileType) => {
  if (!fileType) return 'file'

  if (fileType.startsWith('image/')) return 'file-image'
  if (fileType.startsWith('video/')) return 'file-video'
  if (fileType.startsWith('audio/')) return 'file-audio'
  if (fileType.includes('pdf')) return 'file-pdf'
  if (fileType.includes('word') || fileType.includes('document')) return 'file-word'
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'file-excel'
  if (fileType.includes('zip') || fileType.includes('archive')) return 'file-archive'

  return 'file'
}

const getCurrentLocation = (fieldName = null) => {
  if (!navigator.geolocation) {
    showError('Geolocation wird von diesem Browser nicht unterstützt')
    return
  }

  locationLoading.value = fieldName || true

  navigator.geolocation.getCurrentPosition(
    async (position) => {
      try {
        const { latitude, longitude } = position.coords

        const address = await getAddressFromCoordinates(latitude, longitude)

        if (fieldName) {
          setFieldData(
            fieldName,
            address.address.city
              ? address.address.village
                ? `${address.address.city} OT ${address.address.village}`
                : address.address.city
              : address.address.village,
          )
          locationUsed.value[fieldName] = true

          // Clear validation errors for this field
          // Check all categories to find the field and clear its error
          const categories = ['personal', 'receipt', 'financial']
          categories.forEach((category) => {
            clearFieldError(fieldName, category)
          })

          showSuccess('Standort erfolgreich ermittelt')
        } else {
          formData.value.receiptLocation = address.village
            ? `${address.city} OT ${address.village}`
            : address.city
          showSuccess(`Standort erfolgreich ermittelt: ${address}`)
        }
      } catch (error) {
        console.error('Error getting location:', error)
        showError('Fehler beim Ermitteln des Standorts')
      } finally {
        locationLoading.value = fieldName ? null : false
      }
    },
    (error) => {
      console.error('Geolocation error:', error)
      let errorMessage = 'Fehler beim Ermitteln des Standorts'
      if (error.code === 1) {
        errorMessage =
          'Standort-Zugriff wurde verweigert. Bitte erlaube den Zugriff auf deinen Standort.'
      } else if (error.code === 2) {
        errorMessage = 'Standort konnte nicht ermittelt werden. Bitte versuche es erneut.'
      } else if (error.code === 3) {
        errorMessage = 'Zeitüberschreitung beim Ermitteln des Standorts. Bitte versuche es erneut.'
      }
      showError(errorMessage)
      locationLoading.value = fieldName ? null : false
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000,
    },
  )
}

const getAddressFromCoordinates = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
      {
        headers: {
          'Accept-Language': 'de-DE,de;q=0.9,en;q=0.8',
        },
      },
    )

    if (!response.ok) {
      throw new Error('Reverse geocoding failed')
    }

    const data = await response.json()

    if (data.display_name) {
      return data
    } else {
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
    }
  } catch (error) {
    console.error('Error in reverse geocoding:', error)

    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
  }
}

const hasAdditionalFields = computed(() => {
  return Object.keys(formData.value.data).length > 0
})

const allFieldsData = computed(() => {
  const result = {}
  Object.keys(formData.value.data).forEach((key) => {
    const fieldData = formData.value.data[key]
    if (fieldData && typeof fieldData === 'object' && fieldData.label) {
      result[fieldData.label] = fieldData.data
    } else {
      result[key] = fieldData
    }
  })
  return result
})

const getFormattedFieldValue = (value, fieldName, fieldMeta = null) => {
  const meta =
    fieldMeta ||
    (typeof value === 'object' && value !== null && Object.prototype.hasOwnProperty.call(value, 'data')
      ? value
      : formData.value.data?.[fieldName] || null)
  return formatFieldValue(value, fieldName, meta)
}

// Prüft ob es sich um ein Repeater-Feld handelt
const isRepeaterField = (value) => {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value[0] &&
    typeof value[0] === 'object' &&
    ('quantity' in value[0] || 'description' in value[0] || 'unitPrice' in value[0])
  )
}

// Formatiert Repeater-Felder als Text (für Fallback)
const formatRepeaterFieldValue = (repeaterItems, fieldName = null) => {
  if (!Array.isArray(repeaterItems) || repeaterItems.length === 0) {
    return 'Keine Positionen'
  }

  return repeaterItems
    .map((item, index) => {
      const quantity = item.quantity || 0
      const unit = item.unit || ''
      const description = item.description || 'Keine Beschreibung'
      const unitPrice = parseFloat(item.unitPrice) || 0
      const taxRateId = item.taxRateId || ''

      // Get field for tax rate resolution
      const field = selectedTemplate.value?.template?.fields?.find((f) => f.name === fieldName)

      // Calculate totals
      const subtotal = quantity * unitPrice
      const brutto = calculateRepeaterItemTotal(item, field, fieldName)

      // Format the item
      let itemText = `${description}: ${quantity}${unit ? ' ' + unit : ''} × ${unitPrice.toFixed(2)}€ = ${brutto}€`

      // Add tax rate info if available
      if (field?.options?.repeaterIncludeTaxRate && item.taxRateId) {
        const taxRate = getTaxRateById(item.taxRateId, field, fieldName)
        if (taxRate && taxRate.rate > 0) {
          const netto = calculateRepeaterItemNet(item, field, fieldName)
          const tax = calculateRepeaterItemTax(item, field, fieldName)
          itemText += ` (${taxRate.displayName} ${taxRate.rate}%: Netto ${netto}€, MwSt ${tax}€)`
        }
      }

      return `${index + 1}. ${itemText}`
    })
    .join('\n')
}

const formatWizardTaxRateValue = (taxRateValue, fieldName = null) => {
  if (!fieldName) return taxRateValue
  if (taxRateValue && typeof taxRateValue === 'object') {
    if (taxRateValue.displayName && taxRateValue.rate !== undefined) {
      return `${taxRateValue.displayName} (${taxRateValue.rate}%)`
    }
    if (taxRateValue.data !== undefined) {
      return formatWizardTaxRateValue(taxRateValue.data, fieldName)
    }
    if (taxRateValue.id) {
      return formatWizardTaxRateValue(taxRateValue.id, fieldName)
    }
  }

  const taxRateId = taxRateValue
  if (!taxRateId) return ''

  const baseFieldName = fieldName.replace('_taxRate', '')
  const templateField =
    templateFields.value.find((f) => f.name === baseFieldName) ||
    templateFields.value.find((f) => f.name === fieldName)

  if (!templateField || !templateField.options) {
    return typeof taxRateId === 'string' ? taxRateId : ''
  }

  const findResolvedRate = () => {
    const { options } = templateField
    if (Array.isArray(options.resolvedTaxRates)) {
      const match = options.resolvedTaxRates.find((tr) => tr.id === taxRateId)
      if (match) return match
    }
    if (
      options.selectedTaxRate &&
      (options.selectedTaxRate.id === taxRateId ||
        options.selectedTaxRate.id === taxRateId?.id)
    ) {
      return options.selectedTaxRate
    }
    return null
  }

  const resolvedRate = findResolvedRate()
  if (resolvedRate) {
    return `${resolvedRate.displayName} (${resolvedRate.rate}%)`
  }

  return typeof taxRateId === 'string' ? taxRateId : ''
}

const isWizardFinancialField = (fieldName, fieldMeta = null) => {
  if (fieldMeta?.category) {
    return fieldMeta.category === 'financial'
  }

  const templateField = templateFields.value?.find((f) => f.name === fieldName)
  if (templateField && templateField.category) {
    return templateField.category === 'financial'
  }

  const fieldData = formData.value.data?.[fieldName]
  if (fieldData?.category) {
    return fieldData.category === 'financial'
  }

  return false
}

const { formatFieldValue } = createReceiptFieldFormatter({
  formatCurrency,
  formatDate,
  formatDateTime,
  isFinancialField: isWizardFinancialField,
  formatRepeaterFieldValue: (value, fieldName) => formatRepeaterFieldValue(value, fieldName),
  formatTaxRateValue: (value, fieldName) => formatWizardTaxRateValue(value, fieldName),
})

const formattedFieldsData = computed(() => {
  const result = {}
  Object.keys(formData.value.data).forEach((key) => {
    const fieldData = formData.value.data[key]
    if (
      fieldData &&
      typeof fieldData === 'object' &&
      Object.prototype.hasOwnProperty.call(fieldData, 'data')
    ) {
      const entryKey = fieldData.label || key
      result[entryKey] = {
        value: fieldData,
        fieldName: key,
        groupId: Object.prototype.hasOwnProperty.call(fieldData, 'groupId')
          ? fieldData.groupId
          : null,
      }
    } else {
      result[key] = {
        value: fieldData,
        fieldName: key,
        groupId:
          fieldData && typeof fieldData === 'object' && Object.prototype.hasOwnProperty.call(fieldData, 'groupId')
            ? fieldData.groupId
            : null,
      }
    }
  })
  return result
})

const formattedFieldsDataByCategory = computed(() => {
  const result = {
    personal: {},
    receipt: {},
    financial: {},
  }

  Object.keys(formData.value.data).forEach((key) => {
    const fieldData = formData.value.data[key]
    const templateField = templateFields.value.find(
      (f) => f.name === key || f.name === key.replace('_taxRate', ''),
    )
    const category = fieldData?.category || templateField?.category || 'receipt'
    const fieldInfo = {
      value:
        fieldData && typeof fieldData === 'object' && Object.prototype.hasOwnProperty.call(fieldData, 'data')
          ? fieldData
          : fieldData,
      fieldName: key,
      groupId:
        (fieldData &&
          typeof fieldData === 'object' &&
          Object.prototype.hasOwnProperty.call(fieldData, 'groupId') &&
          fieldData.groupId !== undefined)
          ? fieldData.groupId
          : templateField?.options?.groupId || null,
    }

    const targetKey =
      fieldData &&
      typeof fieldData === 'object' &&
      Object.prototype.hasOwnProperty.call(fieldData, 'label') &&
      fieldData.label
        ? fieldData.label
        : key

    result[category][targetKey] = fieldInfo
  })

  return result
})

const canCreateReceipt = computed(() => {
  return !creating.value
})

watch(currentStep, async (newStep, oldStep) => {
  const signatureStepIndex = getStepIndex('Unterschrift')
  const signatureStep = signatureStepIndex >= 0 ? signatureStepIndex + 1 : null

  if (signatureStep === null) {
    return
  }

  if (oldStep === signatureStep && signatureCanvas.value) {
    saveCanvasContent()
  }

  if (newStep === signatureStep) {
    await nextTick()

    let attempts = 0
    const maxAttempts = 10

    const tryInitCanvas = () => {
      const canvasEl = signatureCanvas.value
      if (canvasEl && typeof canvasEl.getContext === 'function') {
        initializeCanvas()
        if (signatureDataURL.value) {
          setTimeout(() => restoreCanvasContent(), 100)
        }
      } else if (attempts < maxAttempts) {
        attempts++
        setTimeout(tryInitCanvas, 200)
      } else {
        console.error('Canvas not found after', maxAttempts, 'attempts')
      }
    }

    tryInitCanvas()
  }

  if (newStep !== signatureStep && signatureCanvas.value && signatureDataURL.value) {
    await nextTick()
    setTimeout(() => {
      const canvasEl = signatureCanvas.value
      if (canvasEl && typeof canvasEl.getContext === 'function') {
        initializeCanvas()
        restoreCanvasContent()
      }
    }, 100)
  }
})

watch(
  storedSignature,
  async (newSignature) => {
    if (!usingStoredSignature.value) {
      return
    }

    if (!newSignature) {
      clearSignature()
      return
    }

    signatureDataURL.value = newSignature
    hasSignature.value = true

    await nextTick()
    if (signatureCanvas.value) {
      initializeCanvas()
      restoreCanvasContent()
    }
  },
)

const calculateTotalAmount = () => {
  let totalNetto = 0
  let totalBrutto = 0

  if (templateFields.value && formData.value.data) {
    templateFields.value.forEach((field) => {
      if (field.category === 'financial') {
        let computedBrutto = null
        if (field.type === 'money' && field.isCalculated && field.options?.pricePerUnit) {
          const price = parseFloat(field.options.pricePerUnit) || 0
          let quantity = 0
          if (field.options?.dependsOn) {
            const depends = Array.isArray(field.options.dependsOn)
              ? field.options.dependsOn[0]
              : field.options.dependsOn
            if (depends) {
              const qVal = getFieldData(depends)
              quantity = qVal ? parseFloat(qVal) : 0
            }
          } else {
            const selfVal = getFieldData(field.name)
            quantity = selfVal ? parseFloat(selfVal) : 0
          }
          const amount = Math.max(0, quantity * price)
          if (!isNaN(amount)) {
            computedBrutto = Math.round(amount * 100) / 100
          }
        }

        const rawVal = getFieldData(field.name)
        const inputAmount =
          computedBrutto != null
            ? computedBrutto
            : rawVal && !isNaN(parseFloat(rawVal))
              ? parseFloat(rawVal)
              : null

        if (inputAmount != null) {
          if (field.options?.includeTaxRate) {
            const effectiveTaxRate = getEffectiveTaxRate(field.name)
            if (effectiveTaxRate) {
              const taxRate = parseFloat(effectiveTaxRate.rate) / 100
              const bruttoAmount = inputAmount
              const nettoAmount = bruttoAmount / (1 + taxRate)
              totalNetto += nettoAmount
              totalBrutto += bruttoAmount
            } else {
              totalNetto += inputAmount
              totalBrutto += inputAmount
            }
          } else {
            totalNetto += inputAmount
            totalBrutto += inputAmount
          }
        }
      }
    })
  }

  if (personalFields.value && formData.value.data) {
    personalFields.value.forEach((field) => {
      if (field.category === 'financial') {
        const value = getFieldData(field.name)
        if (value && !isNaN(parseFloat(value))) {
          const amount = parseFloat(value)
          totalNetto += amount
          totalBrutto += amount
        }
      }
    })
  }

  // Add repeater fields calculation with proper tax handling
  const repeaterTotals = calculateRepeaterFieldsWithTax()
  totalNetto += repeaterTotals.netto
  totalBrutto += repeaterTotals.brutto

  formData.value.totalAmount = Math.round(totalNetto * 100) / 100
  formData.value.totalAmountWithTax = Math.round(totalBrutto * 100) / 100

  return formData.value.totalAmount
}

const getEffectiveBruttoAmount = (fieldName) => {
  const field = templateFields.value?.find((f) => f.name === fieldName)
  if (!field) return null

  if (field.type === 'money' && field.isCalculated && field.options?.pricePerUnit) {
    const price = parseFloat(field.options.pricePerUnit) || 0
    let quantity = 0
    if (field.options?.dependsOn) {
      const depends = Array.isArray(field.options.dependsOn)
        ? field.options.dependsOn[0]
        : field.options.dependsOn
      if (depends) {
        const qVal = getFieldData(depends)
        quantity = qVal ? parseFloat(qVal) : 0
      }
    } else {
      const selfVal = getFieldData(field.name)
      quantity = selfVal ? parseFloat(selfVal) : 0
    }
    const amount = Math.max(0, quantity * price)
    return isNaN(amount) ? null : Math.round(amount * 100) / 100
  }

  const raw = getFieldData(fieldName)
  if (raw == null || raw === '' || isNaN(parseFloat(raw))) return null
  return parseFloat(raw)
}

const calculateTaxAmount = (fieldName) => {
  const field = templateFields.value.find((f) => f.name === fieldName)
  if (!field || !field.options?.includeTaxRate) {
    return 0
  }

  const value = getEffectiveBruttoAmount(fieldName)
  if (value == null || isNaN(parseFloat(value))) {
    return 0
  }

  const effectiveTaxRate = getEffectiveTaxRate(fieldName)
  if (!effectiveTaxRate) {
    return 0
  }

  const bruttoAmount = parseFloat(value)
  const taxRate = parseFloat(effectiveTaxRate.rate) / 100
  const nettoAmount = bruttoAmount / (1 + taxRate)
  const taxAmount = bruttoAmount - nettoAmount

  return Math.round(taxAmount * 100) / 100
}

const getEffectiveTaxRate = (fieldName) => {
  const field = templateFields.value.find((f) => f.name === fieldName)
  if (!field || !field.options?.includeTaxRate) {
    return null
  }
  const userSelectedTaxRateId =
    getFieldData(`${fieldName}_taxRate`, fieldName) || field.options?.selectedTaxRateId
  if (userSelectedTaxRateId) {
    const userTaxRate = field.options?.resolvedTaxRates?.find(
      (tr) => tr.id === userSelectedTaxRateId,
    )
    if (userTaxRate) {
      return userTaxRate
    }
  }
  return 1
}

const isTaxRateZero = (fieldName) => {
  const field = templateFields.value.find((f) => f.name === fieldName)
  if (!field || !field.options?.includeTaxRate) {
    return true
  }

  const userSelectedTaxRateId = getFieldData(`${fieldName}_taxRate`)
  if (userSelectedTaxRateId) {
    const userTaxRate = field.options?.resolvedTaxRates?.find(
      (tr) => tr.id === userSelectedTaxRateId,
    )
    if (userTaxRate) {
      return parseFloat(userTaxRate.rate) === 0
    }
  }

  if (field.options?.selectedTaxRate) {
    const taxRate = parseFloat(field.options.selectedTaxRate.rate || 0)
    return taxRate === 0
  }

  return true
}

const onTaxRateChange = (fieldName, taxRateId) => {
  calculateTotalAmount()

  nextTick(() => {})
}

const calculateNettoAmount = (fieldName) => {
  const field = templateFields.value.find((f) => f.name === fieldName)
  if (!field || !field.options?.includeTaxRate) {
    const value = getEffectiveBruttoAmount(fieldName)
    return value ? parseFloat(value) : 0
  }

  const value = getEffectiveBruttoAmount(fieldName)
  if (value == null || isNaN(parseFloat(value))) {
    return 0
  }

  const effectiveTaxRate = getEffectiveTaxRate(fieldName)
  if (!effectiveTaxRate) {
    return parseFloat(value)
  }

  const bruttoAmount = parseFloat(value)
  const taxRate = parseFloat(effectiveTaxRate.rate) / 100
  const nettoAmount = bruttoAmount / (1 + taxRate)

  return Math.round(nettoAmount * 100) / 100
}


onMounted(async () => {
  await ensureServerDate()

  await teamsStore.fetchTeams(1, teamsItemsPerPage, '')

  if (sourceReceiptId.value) {
    try {
      recreatingFromReceipt.value = true
      const receiptResponse = await receiptsApi.getReceiptById(sourceReceiptId.value)
      sourceReceipt.value = receiptResponse

      formData.value.teamId = receiptResponse.teamId || null
      formData.value.teamTemplateId = receiptResponse.teamTemplateId || null
      formData.value.recipientEmail = receiptResponse.recipientEmail || ''
      formData.value.recipientName =
        receiptResponse.team?.name || receiptResponse.recipientName || ''

      if (receiptResponse.teamId) {
        let team = userTeams.value?.find((t) => t.id === receiptResponse.teamId)
        if (!team) {
          team = await teamsStore.loadTeamById(receiptResponse.teamId)
        }
        if (!team) {
          throw new Error('team not available')
        }
      }

      if (receiptResponse.teamTemplateId && receiptResponse.teamId) {
        await loadTeamData(receiptResponse.teamId)
      }

      if (!receiptResponse.data) {
        formData.value.data = {}
      } else {
        formData.value.data = normalizeTaxRateFields(receiptResponse.data)
        await nextTick()
      }

      // Do not prefill file upload fields when recreating from an existing receipt
      uploadedFiles.value = {}

      await loadAvailableTemplates()

      // Initialize form data with template fields to ensure issuer_name is set
      await ensureServerDate()
      initializeFormDataWithTemplateFields()

      const fillStepIndex = getStepIndex('Quittung ausfüllen')
      if (fillStepIndex !== -1 && !lostTemplate.value) {
        currentStep.value = fillStepIndex + 1
        initializeSubStep()
        calculateTotalAmount()
      }else{
        lostTemplate.value = false
      }
    } catch (error) {
      console.error(`Fehler beim Laden der ${documentLabel.value} zum Neuerstellen:`, error)
      showError(`Erstellen der ${documentLabel.value} nicht möglich!`)
      await resetRecreationMode()
    }
  }

  const urlParams = new URLSearchParams(window.location.search)
  const teamId = urlParams.get('teamId')
  const teamName = urlParams.get('teamName')
  const fromTeam = urlParams.get('from')

  if (teamId && teamName && fromTeam === 'team') {
    await nextTick()

    let team = userTeams.value?.find((t) => t.id === teamId)

    if (!team && teamsLoading.value) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      team = userTeams.value?.find((t) => t.id === teamId)
    }

    if (team) {
      selectTeam(teamId)

      const newUrl = new URL(window.location)
      newUrl.searchParams.delete('teamId')
      newUrl.searchParams.delete('teamName')
      newUrl.searchParams.delete('from')
      newUrl.searchParams.delete('preselectedTeam')
      window.history.replaceState({}, '', newUrl)
    } else {
      console.warn(`Team with ID ${teamId} not found in user teams`)
    }
  }

  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)

  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
})

const getFieldFilterOptions = (fieldName) => {
  const fieldInfo = availableFilters.value.fieldValues[fieldName]
  if (!fieldInfo || !fieldInfo.values) return []

  return fieldInfo.values.map((value) => ({
    value: value,
    label: value,
    type: fieldInfo.type,
    displayName: fieldInfo.displayName,
  }))
}

const openDatePicker = (event) => {
  const input = event?.target
  if (input && typeof input.showPicker === 'function') {
    try {
      input.showPicker()
    } catch (e) {
      /* ignore */
    }
  }
}

// Repeater Field Functions
const repeaterData = ref({})

const addRepeaterItem = (fieldName) => {
  if (!repeaterData.value[fieldName]) {
    repeaterData.value[fieldName] = []
  }

  const newItem = {
    quantity: 1,
    unit: '',
    description: '',
    unitPrice: 0,
    taxRateId: '',
  }

  repeaterData.value[fieldName].push(newItem)

  // Update fieldData for the repeater field
  fieldData.value[fieldName] = repeaterData.value[fieldName]

  // Update formData for the repeater field
  setFieldData(fieldName, repeaterData.value[fieldName])

  // Clear validation errors for this repeater field
  clearFieldError(fieldName, 'receipt')

  // Also clear any individual item validation errors
  const items = repeaterData.value[fieldName] || []
  items.forEach((_, index) => {
    clearRepeaterError(fieldName, index, 'quantity')
    clearRepeaterError(fieldName, index, 'description')
    clearRepeaterError(fieldName, index, 'unitPrice')
    clearRepeaterError(fieldName, index, 'taxRateId')
  })
}

const removeRepeaterItem = (fieldName, index) => {
  if (repeaterData.value[fieldName] && repeaterData.value[fieldName].length > index) {
    repeaterData.value[fieldName].splice(index, 1)
    fieldData.value[fieldName] = repeaterData.value[fieldName]

    // Update formData for the repeater field
    setFieldData(fieldName, repeaterData.value[fieldName])

    // Clear validation errors for this repeater field
    clearFieldError(fieldName, 'receipt')

    // Also clear any individual item validation errors
    const items = repeaterData.value[fieldName] || []
    items.forEach((_, itemIndex) => {
      clearRepeaterError(fieldName, itemIndex, 'quantity')
      clearRepeaterError(fieldName, itemIndex, 'description')
      clearRepeaterError(fieldName, itemIndex, 'unitPrice')
      clearRepeaterError(fieldName, itemIndex, 'taxRateId')
    })
  }
}

const getRepeaterItems = (fieldName) => {
  return repeaterData.value[fieldName] || []
}

// Watch for changes in repeater data and update formData
watch(
  repeaterData,
  (newData) => {
    Object.keys(newData).forEach((fieldName) => {
      setFieldData(fieldName, newData[fieldName])
      // Clear validation errors for this repeater field
      clearFieldError(fieldName, 'receipt')

      // Also clear any individual item validation errors
      const items = newData[fieldName] || []
      items.forEach((_, index) => {
        clearRepeaterError(fieldName, index, 'quantity')
        clearRepeaterError(fieldName, index, 'description')
        clearRepeaterError(fieldName, index, 'unitPrice')
        clearRepeaterError(fieldName, index, 'taxRateId')
      })
    })
    // Recalculate total amount when repeater data changes
    calculateTotalAmount()
  },
  { deep: true },
)

const clearRepeaterError = (fieldName, index, subField) => {
  const errorKey = `${fieldName}-${index}-${subField}`
  if (errors.value[errorKey]) {
    delete errors.value[errorKey]
  }
}

const updateRepeaterTaxRateSelection = (fieldName, index, taxRateId) => {
  const currentItems = repeaterData.value[fieldName] || []
  if (!currentItems[index]) {
    return
  }

  const templateField =
    selectedTemplate.value?.template?.fields?.find((field) => field.name === fieldName) || null
  const resolvedTaxRate =
    taxRateId && templateField ? getTaxRateById(taxRateId, templateField, fieldName) : null

  const updatedItem = {
    ...currentItems[index],
    taxRateId,
  }

  if (resolvedTaxRate) {
    updatedItem.resolvedTaxRate = resolvedTaxRate
  } else {
    delete updatedItem.resolvedTaxRate
  }

  const updatedItems = [...currentItems]
  updatedItems.splice(index, 1, updatedItem)

  repeaterData.value[fieldName] = updatedItems
  setFieldData(fieldName, updatedItems)
  calculateTotalAmount()
}

const getAvailableTaxRates = (field) => {
  if (!field.options?.repeaterIncludeTaxRate) return []

  if (field.options.repeaterTaxRateSelectionMode === 'user_choice') {
    // Use resolved tax rates if available, otherwise fall back to available tax rate IDs
    if (
      field.options.repeaterResolvedTaxRates &&
      field.options.repeaterResolvedTaxRates.length > 0
    ) {
      return field.options.repeaterResolvedTaxRates
    }
    // Fallback: try to find resolved tax rates from the main field options
    if (field.options.resolvedTaxRates && field.options.resolvedTaxRates.length > 0) {
      const availableIds = field.options.repeaterAvailableTaxRates || []
      return field.options.resolvedTaxRates.filter((tr) => availableIds.includes(tr.id))
    }
    return []
  } else if (field.options.repeaterTaxRateSelectionMode === 'fixed') {
    if (field.options.repeaterSelectedTaxRateId) {
      // Try to find the resolved tax rate
      if (
        field.options.repeaterResolvedTaxRates &&
        field.options.repeaterResolvedTaxRates.length > 0
      ) {
        const selectedRate = field.options.repeaterResolvedTaxRates.find(
          (tr) => tr.id === field.options.repeaterSelectedTaxRateId,
        )
        if (selectedRate) return [selectedRate]
      }
      // Fallback: try to find from main field options
      if (field.options.resolvedTaxRates && field.options.resolvedTaxRates.length > 0) {
        const selectedRate = field.options.resolvedTaxRates.find(
          (tr) => tr.id === field.options.repeaterSelectedTaxRateId,
        )
        if (selectedRate) return [selectedRate]
      }
      // Last fallback: create a placeholder
      return [
        { id: field.options.repeaterSelectedTaxRateId, rate: 0, displayName: 'Fester Steuersatz' },
      ]
    }
    return []
  }

  return []
}

const getTaxRateById = (taxRateId, field, fieldName = null) => {
  if (!taxRateId) {
    return { id: taxRateId, rate: 0, displayName: 'Kein Steuersatz' }
  }
  // If taxRateId is already an object with rate and displayName, return it
  if (typeof taxRateId === 'object' && taxRateId.rate && taxRateId.displayName) {
    return taxRateId
  }

  if (!field) {
    return { id: taxRateId, rate: 19, displayName: 'MwSt' }
  }

  // Try to find in repeater resolved tax rates
  if (
    field.options?.repeaterResolvedTaxRates &&
    field.options.repeaterResolvedTaxRates.length > 0
  ) {
    const taxRate = field.options.repeaterResolvedTaxRates.find((tr) => tr.id === taxRateId)
    if (taxRate) return taxRate
  }

  // Fallback: try to find in main field resolved tax rates
  if (field.options?.resolvedTaxRates && field.options.resolvedTaxRates.length > 0) {
    const taxRate = field.options.resolvedTaxRates.find((tr) => tr.id === taxRateId)
    if (taxRate) return taxRate
  }

  // Try to find in selectedTemplate if we have a fieldName
  if (fieldName && selectedTemplate.value?.template?.fields) {
    const templateField = selectedTemplate.value.template.fields.find((f) => f.name === fieldName)
    if (
      templateField &&
      templateField.type === 'repeater' &&
      templateField.options?.repeaterResolvedTaxRates
    ) {
      const taxRate = templateField.options.repeaterResolvedTaxRates.find(
        (tr) => tr.id === taxRateId,
      )
      if (taxRate) return taxRate
    }
  }

  // Last fallback: return placeholder
  return { id: taxRateId, rate: 19, displayName: 'MwSt' }
}

const getResolvedTaxRate = (taxRateId, fieldData = null, fieldName = null) => {
  if (!taxRateId) return null

  // If taxRateId is already an object with rate and displayName, return it
  if (typeof taxRateId === 'object' && taxRateId.rate && taxRateId.displayName) {
    return taxRateId
  }

  // Try to find in field data resolved tax rates
  if (fieldData && !fieldData.type) {
    const resolvedRate = fieldData.resolvedTaxRate
    if (resolvedRate) return resolvedRate
  }

  if (fieldData && fieldData.resolvedTaxRates) {
    const resolvedRate = fieldData.resolvedTaxRates.find((tr) => tr.id === taxRateId)
    if (resolvedRate) return resolvedRate
  }

  // For repeater fields, try to find in selectedTemplate
  if (selectedTemplate.value?.template?.fields && fieldName) {
    const field = selectedTemplate.value.template.fields.find((f) => f.name === fieldName)
    if (field && field.type === 'repeater' && field.options?.repeaterResolvedTaxRates) {
      const taxRate = field.options.repeaterResolvedTaxRates.find((tr) => tr.id === taxRateId)
      if (taxRate) return taxRate
    }
  }

  // Fallback: return a basic object with the ID
  return { id: taxRateId, rate: 19, displayName: 'MwSt' }
}

const calculateRepeaterItemTotal = (item, field = null, fieldName = null) => {
  const quantity = parseFloat(item.quantity) || 0
  const unitPrice = parseFloat(item.unitPrice) || 0
  const subtotal = quantity * unitPrice

  if (item.taxRateId) {
    // Get the template field for tax rate resolution
    const templateField = fieldName
      ? selectedTemplate.value?.template?.fields?.find((f) => f.name === fieldName)
      : field
    if (templateField?.options?.repeaterIncludeTaxRate) {
      const taxRate = getTaxRateById(item.taxRateId, field, fieldName)
      if (taxRate) {
        // Return brutto amount (subtotal is already brutto)
        return subtotal.toFixed(2)
      }
    }
  }

  return subtotal.toFixed(2)
}

// Calculate net amount for repeater item
const calculateRepeaterItemNet = (item, field = null, fieldName = null) => {
  const quantity = parseFloat(item.quantity) || 0
  const unitPrice = parseFloat(item.unitPrice) || 0
  const subtotal = quantity * unitPrice

  if (item.taxRateId) {
    // Get the template field for tax rate resolution
    const templateField = fieldName
      ? selectedTemplate.value?.template?.fields?.find((f) => f.name === fieldName)
      : field
    if (templateField?.options?.repeaterIncludeTaxRate) {
      const taxRate = getTaxRateById(item.taxRateId, field, fieldName)
      if (taxRate && taxRate.rate) {
        const rate = parseFloat(taxRate.rate) / 100
        const netto = subtotal / (1 + rate)
        return netto.toFixed(2)
      }
    }
  }

  return subtotal.toFixed(2)
}

// Calculate tax amount for repeater item
const calculateRepeaterItemTax = (item, field = null, fieldName = null) => {
  const quantity = parseFloat(item.quantity) || 0
  const unitPrice = parseFloat(item.unitPrice) || 0
  const subtotal = quantity * unitPrice

  if (item.taxRateId) {
    // Get the template field for tax rate resolution
    const templateField = fieldName
      ? selectedTemplate.value?.template?.fields?.find((f) => f.name === fieldName)
      : field
    if (templateField?.options?.repeaterIncludeTaxRate) {
      const taxRate = getTaxRateById(item.taxRateId, field, fieldName)
      if (taxRate && taxRate.rate) {
        const rate = parseFloat(taxRate.rate) / 100
        const netto = subtotal / (1 + rate)
        const tax = subtotal - netto
        return tax.toFixed(2)
      }
    }
  }

  return '0.00'
}

// Calculate total for a repeater field
const calculateRepeaterFieldTotal = (repeaterItems, field, fieldName = null) => {
  if (!Array.isArray(repeaterItems)) return '0.00'

  let total = 0

  repeaterItems.forEach((item) => {
    const itemTotal = parseFloat(calculateRepeaterItemTotal(item, field, fieldName))
    total += itemTotal
  })

  return total.toFixed(2)
}

// Calculate tax breakdown for repeater field
const calculateRepeaterFieldTaxBreakdown = (repeaterItems, field, fieldName = null) => {
  if (!Array.isArray(repeaterItems)) return []

  const taxBreakdown = new Map()

  repeaterItems.forEach((item) => {
    if (item.taxRateId) {
      const templateField = fieldName
        ? selectedTemplate.value?.template?.fields?.find((f) => f.name === fieldName)
        : field
      if (templateField?.options?.repeaterIncludeTaxRate) {
        const taxRate = getTaxRateById(item.taxRateId, field, fieldName)
        if (taxRate && taxRate.rate) {
          const quantity = parseFloat(item.quantity) || 0
          const unitPrice = parseFloat(item.unitPrice) || 0
          const subtotal = quantity * unitPrice

          const rate = parseFloat(taxRate.rate) / 100
          const netto = subtotal / (1 + rate)
          const tax = subtotal - netto

          const key = `${taxRate.displayName} (${taxRate.rate}%)`
          if (taxBreakdown.has(key)) {
            const existing = taxBreakdown.get(key)
            existing.netto += netto
            existing.tax += tax
            existing.brutto += subtotal
          } else {
            taxBreakdown.set(key, {
              displayName: taxRate.displayName,
              rate: taxRate.rate,
              netto: netto,
              tax: tax,
              brutto: subtotal,
            })
          }
        }
      }
    }
  })

  return Array.from(taxBreakdown.values())
}

// Calculate repeater fields with proper tax handling (like financial fields)
const calculateRepeaterFieldsWithTax = () => {
  let totalNetto = 0
  let totalBrutto = 0

  templateFields.value.forEach((field) => {
    if (field.type === 'repeater' && field.category === 'receipt') {
      const repeaterItems = getRepeaterItems(field.name)
      repeaterItems.forEach((item) => {
        const subtotal = item.quantity * item.unitPrice

        const resolvedTaxRate = getResolvedTaxRate(item.taxRateId, null, field.name)
        if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
          const taxRateValue = parseFloat(resolvedTaxRate.rate) / 100
          const bruttoAmount = subtotal
          const nettoAmount = bruttoAmount / (1 + taxRateValue)
          totalNetto += nettoAmount
          totalBrutto += bruttoAmount
        } else {
          totalNetto += subtotal
          totalBrutto += subtotal
        }
      })
    }
  })

  return { netto: totalNetto, brutto: totalBrutto }
}

const wizardContext = {
  authStore,
  currentStep,
  totalSteps,
  canProceed,
  nextStepLoading,
  nextButtonTooltip,
  formData,
  selectPersonal,
  selectTeam,
  teamsLoading,
  userTeams,
  paginatedTeams,
  teamsPaginationData,
  showTeamsPagination,
  showTeamsSearch,
  teamSearchQuery,
  totalTeamPages,
  filteredTeams,
  goToTeamsPage,
  prevTeamsPage,
  nextTeamsPage,
  templatesLoading,
  availableTemplates,
  selectedTemplate,
  documentLabel,
  documentPluralLabel,
  documentSumLabel,
  documentFillStepTitle,
  canCreateReceipt,
  creating,
  previewing,
  selectedTeam,
  selectTemplate,
  getTemplateCategories,
  getCategoryLabel,
  getCategoryIcon,
  isCurrentStep,
  hasAvailableDrafts,
  autoTemplateSelectionInProgress,
  selectedDraft,
  selectManualCreation,
  showDraftSelection,
  signatureCanvas,
  startDrawing,
  draw,
  stopDrawing,
  clearSignature,
  applyStoredSignature,
  hasSignature,
  hasStoredSignature,
  storedSignatureUpdatedAt,
  usingStoredSignature,
  validateRecipientEmail,
  clearFieldError,
  errors,
  locationLoading,
  getCurrentLocation,
  savedSessionIds,
  uploadedFiles,
  handleFileValidationChange,
  saveSessionId,
  fileUploadRefs,
  hasAdditionalFields,
  formattedFieldsDataByCategory,
  getFileCount,
  isFileField,
  getFileIcon,
  getFormattedFieldValue,
  isRepeaterField,
  calculateRepeaterItemTotal,
  calculateRepeaterItemNet,
  calculateRepeaterItemTax,
  calculateRepeaterFieldTotal,
  calculateRepeaterFieldTaxBreakdown,
  getTaxRateById,
  needsSignature,
  signatureDataURL,
  formatAmount,
  formatCurrency,
  getOverallCompletionPercentage,
  getOverallCompletionStatus,
  subSteps,
  currentSubStep,
  isSubStepCompleted,
  getSubStepCompletionStatus,
  previousSubStep,
  nextSubStep,
  previousStep,
  handleNextStep,
  createReceipt,
  previewReceipt,
  isCurrentSubStep,
  templateFieldsByCategory,
  fieldGroupsByCategory,
  groupedTemplateFields,
  fieldData,
  clearRepeaterError,
  getRepeaterItems,
  removeRepeaterItem,
  addRepeaterItem,
  updateRepeaterTaxRateSelection,
  getAvailableTaxRates,
  locationUsed,
}

provide(ReceiptWizardSymbol, wizardContext)
</script>

<style scoped>
.wizard-field-group {
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #ffffff;
  padding: 1.5rem;
}

.group-color-dot {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: inline-block;
}
</style>
